"use client";

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Play,
  CheckCircle,
  Database,
  Book,
  Loader2,
  ArrowRight,
  Check,
  AlertCircle,
} from "lucide-react";
import { apiService } from "@/services/api-service";
import { useSharePointConfig } from "@/contexts/SharePointConfigContext";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface Engagement {
  id: string;
  audit_client_id: string;
  status: string;
  client_name?: string;
  engagement_name?: string;
  [key: string]: any;
}

interface SectionsJson {
  Planning?: Record<string, string>;
  Execution?: Record<string, string>;
  ConcludingProcedures?: Record<string, string>;
}

type Category = "planning" | "execution" | "concluding" | "financial-data" | "libraries";

interface PushLogEntry {
  timestamp: string;
  message: string;
  status: "info" | "success" | "error";
}

interface CustomRollforwardProps {
  onSelect?: (section: string) => void;
  setActiveSection?: (section: string) => void;
}

// â”€â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const CATEGORY_ITEMS: { id: Category; title: string; icon: any; description: string }[] = [
  { id: "planning", title: "Planning", icon: ClipboardList, description: "Planning phase workspaces" },
  { id: "execution", title: "Execution", icon: Play, description: "Audit execution workspaces" },
  { id: "concluding", title: "Concluding Procedures", icon: CheckCircle, description: "Concluding workspaces" },
  { id: "financial-data", title: "Financial Data", icon: Database, description: "Trial Balance & Analytics" },
  { id: "libraries", title: "Libraries", icon: Book, description: "Library data" },
];

const FINANCIAL_DATA_OPTIONS = [
  { id: "trial-balance", label: "Trial Balance" },
  { id: "analytics", label: "Analytics" },
];

const LIBRARY_OPTIONS = [
  { id: "control-library", label: "Control Library" },
  { id: "deficiency-library", label: "Deficiency Library" },
  { id: "misstatement-library", label: "Misstatement Library" },
  { id: "control-owner-library", label: "Control Owner Library" },
  { id: "information-library", label: "Information Library" },
  { id: "document-library", label: "Document Library" },
];

/**
 * Maps workspace â†’ procedure name â†’ the exact db.json section key prefixes
 * (client.section[] starts-with / juggernaut.name starts-with) produced by
 * that specific procedure's components.
 *
 * Sources: INFERENCE IDs found in every substantive-procedure .tsx file,
 * plus the document-card section codes from workspace-document-cards.ts.
 *
 * Workspaces / procedures not listed here fall back to the workspace-level
 * EXECUTION_DB_PREFIX_MAP entry (all files for the workspace are pushed).
 */
const PROCEDURE_DB_PREFIX_MAP: Record<string, Record<string, string[]>> = {
  // â”€â”€ Employee Benefits Expense â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Employee Benefits Expense": {
    "IPE Testing": [
      "Execution_EBE_IPE-TESTING-INFERENCE",
      "Execution_EBE_PayRegistrar", "Execution_EBE_Actuary",
      "Execution_EBE_AL", "Execution_EBE_CTC", "Execution_EBE_DL",
    ],
    "Exception Testing":                 ["Execution_EBE_EXCEPTION-TESTING-INFERENCE"],
    "Headcount Reconciliation":          ["Execution_EBE_HEADCOUNT-RECONCILIATION-INFERENCE"],
    "M-o-M Analysis":                    ["Execution_EBE_MOM-ANALYSIS-INFERENCE"],
    "Increment Analysis":                ["Execution_EBE_INCREMENT-ANALYSIS-INFERENCE"],
    "Substantive Analytical Procedures": ["Execution_EBE_SALARY-ANALYTICAL-INFERENCE"],
    "Actuary Testing":                   ["Execution_EBE_ACTUARY-INFERENCE", "Execution_EBE_Actuary-1", "Execution_EBE_Actuary-2"],
    "Managerial Remuneration":           ["Execution_EBE_MANAGERIAL-REMUNERATION-INFERENCE", "Execution_EBE_ManRem"],
    "PF Analytical":                     ["Execution_EBE_PF-ANALYTICAL-INFERENCE"],
    "ESOP Testing":                      [],
    "Share Based Payment":               ["Execution_EBE_ESOP", "Execution_EBE_ESOP_Cash"],
  },

  // â”€â”€ Property, Plant & Equipment â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Property, Plant & Equipment": {
    "IPE Testing": [
      "Execution_PPE_IPE-TESTING-INFERENCE",
      "Execution_PPE_AL", "Execution_PPE_DL",
      "Execution_PPE_FAR", "Execution_PPE_FARPrev", "Execution_PPE_CWIP",
    ],
    "Exception Testing":           ["Execution_PPE_EXCEPTION-TESTING-PPE-INFERENCE"],
    "Additions & Deletions Analysis": [
      "Execution_PPE_ADL", "Execution_PPE_PPE-ADL-INFERENCE",
      "Execution_PPE_ADLCompAnls", "Execution_PPE_ALCompAnls", "Execution_PPE_DLCompAnls",
    ],
    "CWIP Analysis":               ["Execution_PPE_CWIP-ANALYSIS-INFERENCE", "Execution_PPE_CWIPCompleteness", "Execution_PPE_CWIPCompAnls"],
  },

  // â”€â”€ Intangible Assets â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Intangible Assets": {
    "IPE Testing": [
      "Execution_IT_IPE_Testing",
      "Execution_IT_IntAR", "Execution_IT_AL", "Execution_IT_DL", "Execution_IT_PrevIntAR",
    ],
    "Exception Testing":            ["Execution_IA_EXCEPTION-TESTING-INTANGIBLE-ASSETS-INFERENCE", "Execution_IT_Exception_Testing"],
    "Additions & Deletions Listing":["Execution_IA_IA-ADL-INFERENCE", "Execution_IT_ADL", "Execution_IT_AL", "Execution_IT_DL"],
    "Intangible Assets Under Development": ["Execution_IA_INVSMT-INFERENCE"],
  },

  // â”€â”€ Lease Liability â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Lease Liability": {
    "Lease Assessment": ["Execution_LEASE_LEASE-ASSESSMENT-INFERENCE", "Execution_LEASE_LeaseAssessment"],
    "Finance Lease":    ["Execution_LEASE_FINANCE-LEASE-INFERENCE", "Execution_LEASE_FinResponse"],
    "Operating Lease":  ["Execution_LEASE_OPERATING-LEASE-INFERENCE", "Execution_LEASE_OprResponse"],
    "Summary":          [],
    "Ageing Analysis":  ["Execution_LEASE_Ageing"],
  },
  "Lease Liability - Ind AS": {
    "Lease Assessment": ["Execution_LEASE_LEASE-ASSESSMENT-INFERENCE", "Execution_LEASE_LeaseAssessment"],
    "Finance Lease":    ["Execution_LEASE_FINANCE-LEASE-INFERENCE", "Execution_LEASE_FinResponse"],
    "Operating Lease":  ["Execution_LEASE_OPERATING-LEASE-INFERENCE", "Execution_LEASE_OprResponse"],
    "Summary":          [],
    "Ageing Analysis":  ["Execution_LEASE_Ageing"],
  },

  "Right-of-Use Assets": {
    "ROU Disclosure": [],
  },

  // â”€â”€ Trade Payables â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Trade Payables": {
    "IPE Testing":      ["Execution_TP_TPR", "Execution_TP_VM"],
    "Exception Testing":["Execution_TP_EXCEPTION-TESTING-TP-INFERENCE"],
    "Ratio Analysis":   ["Execution_TP_TP-RATIO-INFERENCE"],
    "Ageing Analysis":  ["Execution_TP_Ageing"],
  },

  // â”€â”€ Inventory â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Inventory": {
    "IPE Testing":          ["Execution_INV_IR", "Execution_INV_MCR", "Execution_INV_SSR"],
    "Exception Testing":    ["Execution_INV_EXCEPTION-TESTING-INVENTORY-INFERENCE"],
    "NRV Assessment":       ["Execution_INV_NRV-ASSESSMENT-INFERENCE"],
    "Cost Allocation":      ["Execution_INV_COST-ALLOCATION-INFERENCE", "Execution_INV_CstAloc"],
    "Inventory Ageing":     ["Execution_INV_INVENTORY-AGEING-INFERENCE"],
    "Physical Verification":["Execution_INV_PHYSICAL-VERIFICATION-INFERENCE"],
    "Overhead Working":     [],
    "Ageing Analysis":      ["Execution_INV_Ageing"],
  },

  // â”€â”€ Cash & Cash Equivalents â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Cash & Cash Equivalents": {
    "Bank Accounts":   [],
    "Ageing Analysis": ["Execution_CCE_Ageing"],
  },

  // â”€â”€ Income Tax â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Income Tax": {
    "Current tax computation":    [],
    "Deferred tax computation":   [],
    "ATPT reconciliation":        [],
    "Effective Tax reconciliation": [],
  },

  // â”€â”€ Investments â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Investments": {
    "Investments Procedures": ["Execution_INVST_INVSMT-INFERENCE"],
    "Valuation":              [],
  },

  // â”€â”€ Other Assets â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Other Assets": {
    "IPE Testing":     ["Execution_OTAS_PrepExpLis"],
    "Prepaid Expense": ["Execution_OTAS_PREPAID-EXPENSE-INFERENCE", "Execution_OTAS_PrepExpLis"],
    "Security Deposit":[],
    "Advance to Suppliers - Completeness Check": [
      "Execution_OTAS_ATS",
      "Execution_OTAS_ATS_Response",
    ],
    "Capital Advances - Completeness Check": [
      "Execution_OTAS_CAL",
      "Execution_OTAS_CAL_Response",
    ],
  },

  // â”€â”€ Other Income â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Other Income": {
    "Net Investment in Leases":["Execution_OTINC_NET-INVESTMENT-IN-LEASES-INFERENCE"],
  },

  // â”€â”€ Other Liabilities â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Other Liabilities": {
    "Advance from customer - Completeness Check":["Execution_OTL_AFC", "Execution_OTL_AFC_Response"],
    "Advance from customer - Ageing Analysis":    ["Execution_OTL_AFC"],
    "Capex Vendors - Completeness Check":         ["Execution_OTL_PTP", "Execution_OTL_PTP_Response"],
    "Capex Vendors - Ageing Analysis":            ["Execution_OTL_PTP"],
    "Ageing Analysis":                            ["Execution_OTL_Ageing"],
  },

  "Other Financial Liabilities": {
    "Capex Vendors - Completeness Check": ["Execution_OFL_PTP", "Execution_OFL_PTP_Response"],
    "Ageing Analysis":                    ["Execution_OFL_Ageing"],
  },

  // â”€â”€ Other Receivables â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Other Receivables": {
    "Advance to Suppliers - Completeness Check":["Execution_OTR_ATS", "Execution_OTR_ATS_Response"],
    "Advance to Suppliers - Ageing Analysis":   ["Execution_OTR_ATS"],
    "Capital Advances - Completeness Check":    ["Execution_OTR_CAL", "Execution_OTR_CAL_Response"],
    "Capital Advances - Ageing Analysis":       ["Execution_OTR_CAL"],
    "Ageing Analysis":                          ["Execution_OTR_Ageing"],
  },

  // â”€â”€ Opening Balance Testing â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Opening Balance Testing": {
    "Inquiry with Predecessor Auditor": ["Execution_OBT_OBT-SUB-INFERENCE-INQUIRY"],
    "SA 510 Assessment":                ["Execution_OBT_OBT-SUB-INFERENCE-SA510"],
  },

  // â”€â”€ Related Party Disclosure â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Related Party Disclosure": {
    "Compliance":            ["Execution_RPT_COMPLIANCE-INFERENCE"],
    "Arms-Length Assessment":["Execution_RPT_ARMS-LENGTH-INFERENCE"],
  },

  // â”€â”€ Equity â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Equity": {},

  // â”€â”€ Management Use of Expert â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Management Use of Expert": {
    "Management Use of Expert Procedures": ["Execution_MUE_MUE-INFERENCE"],
    "Assessment":                          ["Execution_MUE_MANAGEMENT-USE-OF-EXPERT-ASSESSMENT-INFERENCE"],
    "Other Procedures":                    ["Execution_MUE_MUE-INFERENCE"],
  },

  // â”€â”€ Use of Auditor's Specialist â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Use of Auditor's Specialist": {},

  // â”€â”€ Depreciation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Depreciation": {
    "Depreciation on ROU":               ["Execution_DEPR_DEPR-ROU-INFERENCE"],
    "Depreciation on Tangible Assets":   ["Execution_DEPR_DEPRECIATION-TANGIBLE-ASSETS-INFERENCE"],
    "Depreciation on Intangible Assets": ["Execution_DEPR_DEPRECIATION-INTANGIBLE-ASSETS-INFERENCE"],
  },

  // â”€â”€ Impairment Testing â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Impairment Testing": {},

  // â”€â”€ Trade Receivables â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Trade Receivables": {
    "IPE Testing":      ["Execution_TR_TRR", "Execution_TR_CM"],
    "Exception Testing":["Execution_TR_EXCEPTION-TESTING-TR-INFERENCE"],
    "Ratio Analysis":   ["Execution_TR_TR-RATIO-INFERENCE"],
    "Ageing Analysis":  ["Execution_TR_Ageing"],
  },

  // â”€â”€ Other Expenses â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Other Expenses": {
    "IPE Testing":                    ["Execution_OE_IPE_Testing"],
    "Analytical Procedures":          ["Execution_OE_Analytical_Procedures"],
    "Corporate Social Responsibility":["Execution_OE_Corporate_Social_Responsibility", "Execution_OE_OTEX-INFERENCE"],
  },

  // â”€â”€ Borrowings â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Borrowings": {
    "Repayment testing":        ["Execution_DEBT_RepaymentTesting-INFERENCE"],
    "Covenant testing":         ["Execution_DEBT_CovenantTesting-INFERENCE"],
    "Drawing Power":            ["Execution_DEBT_DrawingPower-INFERENCE"],
    "Agreement Summary":        ["Execution_DEBT_AgreementSummary-INFERENCE"],
    "Quarterly Stock Statement":["Execution_DEBT_QuarterlyStockStatement-INFERENCE"],
    "Classification testing":   ["Execution_DEBT_ClassificationTesting-INFERENCE"],
  },

  // â”€â”€ Cost of Materials Consumed â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Cost of Materials Consumed": {
    "IPE Testing":        ["Execution_COGS_PurchaseRegister"],
    "Exception Testing":  ["Execution_COMC_EXCEPTION-TESTING-COMC-INFERENCE"],
    "Analytical Procedures": [],
    "Predictive Analytics": [],
  },

  // â”€â”€ Provisions & Contingent Liabilities â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Provisions & Contingent Liabilities": {
    "Other Provisions":["Execution_PCL_PCL-Other-Restructuring", "Execution_PCL_PCL-Other-Restoration", "Execution_PCL_PCL-INFERENCE"],
  },

  // â”€â”€ Litigations & Claims â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Litigations & Claims": {
    "Assessment": ["Execution_LIT_LIT-ASSESS-01", "Execution_LIT_LIT-ASSESS-02-doc", "Execution_LIT_LIT-ASSESS-03-doc"],
  },

  // â”€â”€ Going Concern â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Going Concern": {
    "Assessment": ["ConcludingProcedures_GC_GC-SP-INFERENCE"],
  },

  // â”€â”€ Incentives â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Incentives": {
    "Government Grant": [],
    "Ageing Analysis":  ["Execution_INCT_Ageing"],
  },

  // â”€â”€ Valuation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Valuation": {
    "Ind AS 113": [],
  },

  // â”€â”€ Specialists â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  "Specialists": {
    "ABC": [],
  },
};

const PLANNING_PREFIX_MAP: Record<string, string> = {
  "Engagement Acceptance & Continuance": "EAC",
  "Understanding the Entity & its Environment": "UEE",
  "Materiality": "MAT",
  "Preliminary Analytical Procedures": "PAP",
  "Fraud Risk Assessment": "FRA",
  "IT Risk Assessment": "ITRA",
};

const EXECUTION_PREFIX_MAP: Record<string, string> = {
  "Lease Liability": "LEASE",
  "Lease Liability - Ind AS": "LEASE",
  "Right-of-Use Assets": "ROU",
  "Cash & Cash Equivalents": "CCE",
  "Finance Cost": "FINCO",
  "Income Tax": "ITAX",
  "Investments": "INVST",
  "Other Income": "OTINC",
  "Related Party Disclosure": "RPT",
  "Equity": "EQU",
  "Management Use of Expert": "MUE",
  "Use of Auditor's Specialist": "UAS",
  "Depreciation": "DEPR",
  "Impairment Testing": "IMPR",
  "Other Expenses": "OTEX",
  "Borrowings": "DEBT",
  "Secretarial Compliance": "SECR",
  "Incentives": "INCT",
  "Valuation": "VAL",
  "Contract Assets": "CONA",
  "Provisions & Contingent Liabilities": "PCL",
  "Opening Balance Testing": "OBT",
  "Management Override of Controls": "MOC",
  "Financial Closing & Reporting Process": "FCRP",
  "Other Assets": "OTAS",
  "Employee Benefits Expense": "EBE",
  "Trade Payables": "TP",
  "Inventory": "INV",
  "Other Liabilities": "OTL",
  "Other Financial Liabilities": "OFL",
  "Other Receivables": "OTR",
  "Trade Receivables": "TR",
  "Cost of Materials Consumed": "COGS",
  "Property, Plant & Equipment": "PPE",
  "Intangible Assets": "IT",
};

/**
 * Maps each Execution workspace to ALL db.json prefix patterns its files
 * are stored under (client.section[] and juggernaut.name).
 *
 * Standard pattern for every workspace: Execution_<shortCode>_*
 * Several workspaces also use a legacy/alternate short code found throughout
 * the codebase (DocumentManager, Landing components, workspace-document-cards).
 */
const EXECUTION_DB_PREFIX_MAP: Record<string, string[]> = {
  "Lease Liability":                      ["Execution_LEASE"],
  "Lease Liability - Ind AS":             ["Execution_LEASE"],
  "Right-of-Use Assets":                  ["Execution_ROU"],
  "Cash & Cash Equivalents":               ["Execution_CCE"],
  "Finance Cost":                          ["Execution_FINCO"],
  "Income Tax":                            ["Execution_ITAX"],
  "Investments":                           ["Execution_INVST"],
  // OTINC is the canonical code; OI_ is an older abbreviation used in some response files
  "Other Income":                          ["Execution_OTINC", "Execution_OI"],
  "Related Party Disclosure":              ["Execution_RPT"],
  "Equity":                                ["Execution_EQU"],
  "Management Use of Expert":              ["Execution_MUE"],
  "Use of Auditor's Specialist":           ["Execution_UAS"],
  "Depreciation":                          ["Execution_DEPR"],
  "Impairment Testing":                    ["Execution_IMPR"],
  // OTEX is canonical; OE_ is used by substantive-procedure files in DocumentManager
  "Other Expenses":                        ["Execution_OTEX", "Execution_OE"],
  "Borrowings":                            ["Execution_DEBT"],
  "Secretarial Compliance":                ["Execution_SECR"],
  "Incentives":                            ["Execution_INCT"],
  "Valuation":                             ["Execution_VAL"],
  "Contract Assets":                       ["Execution_CONA"],
  "Provisions & Contingent Liabilities":   ["Execution_PCL"],
  "Opening Balance Testing":               ["Execution_OBT"],
  "Management Override of Controls":       ["Execution_MOC"],
  "Financial Closing & Reporting Process": ["Execution_FCRP"],
  // OTAS is canonical; OA_ is used by response/analysis files
  "Other Assets":                          ["Execution_OTAS", "Execution_OA"],
  "Employee Benefits Expense":             ["Execution_EBE"],
  "Trade Payables":                        ["Execution_TP"],
  "Inventory":                             ["Execution_INV"],
  "Other Liabilities":                     ["Execution_OTL"],
  "Other Financial Liabilities":           ["Execution_OFL"],
  "Other Receivables":                     ["Execution_OTR"],
  "Trade Receivables":                     ["Execution_TR"],
  // COGS is canonical; COMC_ is used by the Cost-of-Materials component
  "Cost of Materials Consumed":            ["Execution_COGS", "Execution_COMC"],
  "Property, Plant & Equipment":           ["Execution_PPE"],
  // IT is canonical; IA_ is used by some Intangible-Assets response/analysis files
  "Intangible Assets":                     ["Execution_IT", "Execution_IA"],
};

const CONCLUDING_PREFIX_MAP_SHORT: Record<string, string> = {
  "CARO": "CARO",
  "Audit Report & Opinion": "AUDREP",
  "Communication with those charged with governance": "CTCWG",
  "Subsequent Events": "SE",
  "Litigations & Claims": "LIT",
  "Compliance with laws and regulations": "CLR",
  "Review of estimates for management bias": "REMB",
  "Internal Audit": "ITAUD",
  "Going Concern": "GC",
  "Evaluate Audit Results Including Misstatements": "EARM",
  "Obtain Written Representations": "WR",
};

// Sub-sections for each Planning workspace (id = question prefix, label = display name)
interface PlanningSubSection {
  id: string;       // used as key in selectedPushItems, e.g. "EP"
  label: string;    // display name
  prefix: string;   // question ID prefix to match, e.g. "EP-"
}

const PLANNING_SUBSECTIONS: Record<string, PlanningSubSection[]> = {
  "Engagement Acceptance & Continuance": [
    { id: "EP", label: "Engagement Profiling", prefix: "EP-" },
    { id: "CE", label: "Compliance with Ethical Requirements", prefix: "CE-" },
    { id: "SR", label: "Scope of Review", prefix: "SR-" },
    { id: "ER", label: "Assess Engagement Risk", prefix: "ER-" },
    { id: "AE", label: "Agreeing the Terms of Audit Engagement", prefix: "AE-" },
  ],
  "Understanding the Entity & its Environment": [
    { id: "EF", label: "External Factors", prefix: "EF-" },
    { id: "IF", label: "Internal Factors", prefix: "IF-" },
    { id: "AP", label: "Accounting Policies", prefix: "AP-" },
    { id: "OC", label: "Other Considerations", prefix: "OC-" },
    { id: "UIC", label: "Understanding Internal Controls", prefix: "UIC-" },
  ],
  "Fraud Risk Assessment": [
    { id: "FRA", label: "Inquiries of the management and others within the entity", prefix: "FRA-" },
    { id: "PD", label: "Planning Discussion with the Client/Entity", prefix: "PD-" },
    { id: "FRF", label: "Fraud Risk Factors", prefix: "FRF-" },
    { id: "MOC", label: "Management Override of Controls", prefix: "MOC-" },
    { id: "CMG", label: "Communication with management and those charged with governance", prefix: "CMG-" },
  ],
};

// All question IDs belonging to Materiality (push as-is)
const MATERIALITY_QUESTION_IDS = [
  "M-1", "M-2", "M-3", "M-4", "M-5", "M-6", "M-7", "M-8",
  "M-9", "M-10", "M-11", "M-13", "M-14", "M-15",
  "abcotdTable", "revisedMaterialityTable", "components",
];

// All question IDs for Preliminary Analytical Procedures (push as-is)
const PAP_QUESTION_IDS = [
  "PreliminaryAnalyticalProcedures_BalanceSheetItems",
  "PreliminaryAnalyticalProcedures_ProfitLossItems",
  "PreliminaryAnalyticalProcedures_Documentation",
];

// IT Risk Assessment question IDs
const IT_RISK_QUESTION_IDS = ["ITR-1", "ITR-2"];

const CONCLUDING_PROCEDURES_WITH_DIVISIONS = ["Litigations & Claims", "Going Concern"];

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const spApi = () => (window as any).sharePointAPI;

async function downloadJsonFromFy(fyYear: string, fileName: string, folder?: string) {
  const api = spApi();
  if (!api?.customRfDownloadJson) throw new Error("customRfDownloadJson API not available");
  const res = await api.customRfDownloadJson({ fyYear, fileName, folder });
  if (!res.success) throw new Error(res.error || "Download failed");
  return res.data;
}

async function uploadJsonToFy(fyYear: string, fileName: string, data: any, folder?: string) {
  const api = spApi();
  if (!api?.customRfUploadJson) throw new Error("customRfUploadJson API not available");
  const res = await api.customRfUploadJson({ fyYear, fileName, data, folder });
  if (!res.success) throw new Error(res.error || "Upload failed");
  return res.data;
}

async function copyFileBetweenFy(
  sourceFyYear: string,
  destFyYear: string,
  sourceFolder: string,
  destFolder: string,
  fileName: string,
  destSections?: string[],
  reference?: string,
) {
  const api = spApi();
  if (!api?.customRfCopyFile) throw new Error("customRfCopyFile API not available");
  const res = await api.customRfCopyFile({
    sourceFyYear,
    destFyYear,
    sourceFolder,
    destFolder,
    fileName,
    destSections,
    reference,
  });
  if (!res.success) throw new Error(res.error || "Copy failed");
  return res.data;
}

async function getDbFromFy(fyYear: string) {
  const api = spApi();
  if (!api?.customRfGetDb) throw new Error("customRfGetDb API not available");
  const res = await api.customRfGetDb({ fyYear });
  if (!res.success) throw new Error(res.error || "Get db failed");
  return res.data;
}

async function rebuildDbUrlsForFy(destFyYear: string) {
  const api = spApi();
  if (!api?.customRfRebuildDbUrls) throw new Error("customRfRebuildDbUrls API not available");
  const res = await api.customRfRebuildDbUrls({ destFyYear });
  if (!res.success) throw new Error(res.error || "Rebuild db URLs failed");
  return res;
}

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export default function CustomRollforward({ onSelect, setActiveSection }: CustomRollforwardProps) {
  const { config } = useSharePointConfig();
  const sourceFyYear = config?.FY_YEAR || "";

  // State
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [selectedEngagementId, setSelectedEngagementId] = useState<string>("");
  const [destFyYear, setDestFyYear] = useState<string>("");
  const [sectionsJson, setSectionsJson] = useState<SectionsJson | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category>("planning");
  const [expandedCategory, setExpandedCategory] = useState<string | null>("planning");
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>("");
  const [selectedSubItem, setSelectedSubItem] = useState<string>("");

  // Execution / Concluding sub-items
  const [execSubView, setExecSubView] = useState<string>("all");

  // (per-procedure selection removed â€” substantive procedures always push the full workspace)

  // Libraries sub-state
  const [libraryType, setLibraryType] = useState<string>("");
  const [librarySubItems, setLibrarySubItems] = useState<any[]>([]);
  const [selectedLibraryItems, setSelectedLibraryItems] = useState<Set<string>>(new Set());

  // Financial Data sub-state
  const [finDataOption, setFinDataOption] = useState<string>("");

  // Push state
  const [isPushing, setIsPushing] = useState(false);
  const [pushLog, setPushLog] = useState<PushLogEntry[]>([]);
  const [selectedPushItems, setSelectedPushItems] = useState<Set<string>>(new Set());

  // Planning data
  const [planningResponses, setPlanningResponses] = useState<any>(null);

  // Execution data
  const [tailoringResponses, setTailoringResponses] = useState<any>(null);
  const [rommLibrary, setRommLibrary] = useState<any[]>([]);
  const [substantiveProcedures, setSubstantiveProcedures] = useState<any>(null);

  // Concluding Procedures data
  const [concludingTailoringResponses, setConcludingTailoringResponses] = useState<any>(null);

  // Source db.json
  const [sourceDb, setSourceDb] = useState<any>(null);

  // â”€â”€â”€ Load engagements â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Only show active engagements for the same client as the current one (DOC_LIBRARY in sharepoint_config).
  const currentClientName = (config?.DOC_LIBRARY ?? "").trim();

  useEffect(() => {
    const loadEngagements = async () => {
      try {
        const all = await apiService.getUserEngagements({ for_main: true });
        const active = (all || []).filter(
          (e) =>
            e.status?.toLowerCase() === "active" &&
            e.engagement_name !== sourceFyYear &&
            currentClientName !== "" &&
            e.client_name != null &&
            String(e.client_name).trim() === currentClientName,
        );
        setEngagements(active);
      } catch (err) {
        console.error("Failed to load engagements", err);
      }
    };
    loadEngagements();
  }, [sourceFyYear, currentClientName]);

  // â”€â”€â”€ Load sections.json â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  useEffect(() => {
    const loadSections = async () => {
      try {
        const api = spApi();
        if (!api?.downloadSectionsJson) return;
        const res = await api.downloadSectionsJson();
        if (res?.success && res.data) {
          setSectionsJson(res.data);
        }
      } catch (err) {
        console.error("Failed to load sections.json", err);
      }
    };
    loadSections();
  }, []);

  // â”€â”€â”€ Load source data when workspace changes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  useEffect(() => {
    if (!sourceFyYear) return;
    const loadSourceData = async () => {
      try {
        if (activeCategory === "planning") {
          const data = await downloadJsonFromFy(sourceFyYear, "Planning_UserResponses.json");
          setPlanningResponses(data);
        }
        if (activeCategory === "execution" || activeCategory === "concluding" || activeCategory === "planning") {
          const [tailor, concludingTailor, romm, subProc, db] = await Promise.all([
            downloadJsonFromFy(sourceFyYear, "Execution_TailoringQuestions_Responses.json").catch(() => null),
            downloadJsonFromFy(sourceFyYear, "ConcludingProcedures_TailoringQuestions_Responses.json").catch(() => null),
            downloadJsonFromFy(sourceFyYear, "Libraries_Romm.json").catch(() => null),
            downloadJsonFromFy(sourceFyYear, "SubstantiveProcedures.json").catch(() => null),
            getDbFromFy(sourceFyYear).catch(() => null),
          ]);
          setTailoringResponses(tailor);
          setConcludingTailoringResponses(concludingTailor);
          setRommLibrary(romm?.romm_library || []);
          setSubstantiveProcedures(subProc);
          setSourceDb(db);
        }
        if (activeCategory === "libraries") {
          const db = await getDbFromFy(sourceFyYear).catch(() => null);
          setSourceDb(db);
        }
      } catch (err) {
        console.error("Failed to load source data", err);
      }
    };
    loadSourceData();
  }, [activeCategory, sourceFyYear]);


  // Handle engagement selection
  const handleEngagementSelect = useCallback(
    (engId: string) => {
      setSelectedEngagementId(engId);
      const eng = engagements.find((e) => e.id === engId);
      setDestFyYear(eng?.engagement_name || "");
    },
    [engagements],
  );

  // â”€â”€â”€ Log helper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const addLog = useCallback((message: string, status: "info" | "success" | "error" = "info") => {
    setPushLog((prev) => [
      ...prev,
      { timestamp: new Date().toLocaleTimeString(), message, status },
    ]);
  }, []);

  // â”€â”€â”€ Get workspaces for a category â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const getWorkspaces = (cat: Category): { name: string; prefix: string }[] => {
    if (cat === "planning") {
      const planning = sectionsJson?.Planning && typeof sectionsJson.Planning === "object" ? sectionsJson.Planning : {};
      if (Object.keys(planning).length === 0) return [];
      return (Object.entries(planning) as [string, string][])
        .filter(([name]) => name !== "Independence")
        .map(([name, prefix]) => ({ name, prefix }));
    }
    if (cat === "execution") {
      const execution = sectionsJson?.Execution && typeof sectionsJson.Execution === "object" ? sectionsJson.Execution : {};
      if (Object.keys(execution).length === 0) return [];
      return Object.entries(execution).map(([name, prefix]) => ({ name, prefix: prefix as string }));
    }
    if (cat === "concluding") {
      const concluding = sectionsJson?.ConcludingProcedures && typeof sectionsJson.ConcludingProcedures === "object" ? sectionsJson.ConcludingProcedures : {};
      if (Object.keys(concluding).length === 0) return [];
      return Object.entries(concluding).map(([name, prefix]) => ({ name, prefix: prefix as string }));
    }
    return [];
  };

  // Left panel: only show section-based categories that exist in Sections.json
  const visibleCategoryItems = useMemo(() => {
    const hasPlanning =
      sectionsJson?.Planning &&
      typeof sectionsJson.Planning === "object" &&
      Object.keys(sectionsJson.Planning).filter((n) => n !== "Independence").length > 0;
    const hasExecution =
      sectionsJson?.Execution &&
      typeof sectionsJson.Execution === "object" &&
      Object.keys(sectionsJson.Execution).length > 0;
    const hasConcluding =
      sectionsJson?.ConcludingProcedures &&
      typeof sectionsJson.ConcludingProcedures === "object" &&
      Object.keys(sectionsJson.ConcludingProcedures).length > 0;
    return CATEGORY_ITEMS.filter((cat) => {
      if (cat.id === "planning") return !!hasPlanning;
      if (cat.id === "execution") return !!hasExecution;
      if (cat.id === "concluding") return !!hasConcluding;
      return true; // financial-data, libraries always visible
    });
  }, [sectionsJson]);

  useEffect(() => {
    const ids = visibleCategoryItems.map((c) => c.id);
    if (ids.length === 0) return;
    if (!ids.includes(activeCategory)) {
      const first = ids[0];
      setActiveCategory(first);
      setExpandedCategory(first);
      setSelectedWorkspace("");
      setFinDataOption("");
      setLibraryType("");
    }
  }, [visibleCategoryItems, activeCategory]);

  // â”€â”€â”€ Get procedures for a workspace â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const getProceduresForWorkspace = (workspaceName: string): string[] => {
    if (!substantiveProcedures || !substantiveProcedures[workspaceName]) return [];
    return Object.keys(substantiveProcedures[workspaceName]);
  };

  // â”€â”€â”€ Get ROMMs for a workspace â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const getRommsForWorkspace = (workspaceName: string) => {
    return rommLibrary.filter((r) => r.workspace === workspaceName);
  };

  // â”€â”€â”€ PUSH LOGIC â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handlePush = async () => {
    if (!destFyYear || !sourceFyYear) {
      addLog("Please select a destination engagement", "error");
      return;
    }

    setIsPushing(true);
    setPushLog([]);
    addLog(`Starting push from "${sourceFyYear}" to "${destFyYear}"...`);

    try {
      if (activeCategory === "planning") {
        await pushPlanning();
      } else if (activeCategory === "execution") {
        await pushExecution();
      } else if (activeCategory === "concluding") {
        await pushConcluding();
      } else if (activeCategory === "financial-data") {
        await pushFinancialData();
      } else if (activeCategory === "libraries") {
        await pushLibraries();
      }

      // Refresh every db.json URL to point to the destination FY folder
      addLog("Updating db.json URLs for destination engagement...");
      try {
        const rebuildResult = await rebuildDbUrlsForFy(destFyYear);
        addLog(`db.json URLs updated (${rebuildResult.updated ?? 0} entr${(rebuildResult.updated ?? 0) === 1 ? "y" : "ies"} refreshed)`, "success");
      } catch (rebuildErr: any) {
        addLog(`db.json URL update failed: ${rebuildErr.message}`, "error");
      }

      addLog("Push completed successfully!", "success");
    } catch (err: any) {
      addLog(`Push failed: ${err.message}`, "error");
    } finally {
      setIsPushing(false);
    }
  };

  // â”€â”€â”€ Push Planning â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const pushPlanning = async () => {
    if (!selectedWorkspace) {
      addLog("No workspace selected", "error");
      return;
    }

    const prefix = PLANNING_PREFIX_MAP[selectedWorkspace] ||
      sectionsJson?.Planning?.[selectedWorkspace] || "";

    addLog(`Pushing Planning workspace: ${selectedWorkspace} (prefix: ${prefix})`);

    // Determine which question IDs to push based on selection
    const sourceResponses = planningResponses?.responses || {};
    let questionIdsToPush: string[] = [];

    if (selectedWorkspace === "Materiality") {
      // Push all Materiality IDs that exist in source
      questionIdsToPush = MATERIALITY_QUESTION_IDS.filter((id) => id in sourceResponses);
      addLog(`Pushing ${questionIdsToPush.length} Materiality response(s)...`);
    } else if (selectedWorkspace === "Preliminary Analytical Procedures") {
      // Push all PAP IDs that exist in source
      questionIdsToPush = PAP_QUESTION_IDS.filter((id) => id in sourceResponses);
      addLog(`Pushing ${questionIdsToPush.length} Preliminary Analytical response(s)...`);
    } else if (selectedWorkspace === "IT Risk Assessment") {
      // Push IT Risk IDs that exist in source
      questionIdsToPush = IT_RISK_QUESTION_IDS.filter((id) => id in sourceResponses);
      addLog(`Pushing ${questionIdsToPush.length} IT Risk Assessment response(s)...`);
    } else if (!(selectedWorkspace in PLANNING_PREFIX_MAP)) {
      // ── Custom workspace: execution-style push ─────────────────────
      const items = selectedPushItems;
      if (items.size === 0) {
        addLog("No options selected. Select at least one item (Tailoring Questions, ROMMs, or Substantive Procedures) to push.", "error");
        return;
      }
      if (items.has("tailoring")) {
        await pushTailoringQuestions(selectedWorkspace);
      }
      if (items.has("romms")) {
        await pushRomms(selectedWorkspace);
      }
      if (items.has("procedures")) {
        await pushSubstantiveProcedures(selectedWorkspace, prefix, new Set<string>());
        // Push planning-specific db files (Planning_<prefix>_* and planning-substantive: pattern)
        const planningDbPrefixes = [
          ...(prefix ? [`Planning_${prefix}`] : [`Planning_${selectedWorkspace}`]),
          `planning-substantive:${selectedWorkspace}`,
        ];
        await pushDbFilesForPrefix(planningDbPrefixes);
      }
      await ensureSectionInDest("Planning", selectedWorkspace, prefix);
      addLog(`Planning "${selectedWorkspace}" push complete`, "success");
      return;
    } else {
      // â”€â”€ Standard workspaces with sub-sections (EAC, UEE, FRA) â”€â”€â”€â”€â”€
      const subSections = PLANNING_SUBSECTIONS[selectedWorkspace] || [];
      if (selectedPushItems.size === 0) {
        // Nothing selected â†’ push all sub-sections
        questionIdsToPush = Object.keys(sourceResponses).filter((id) =>
          subSections.some((ss) => id.startsWith(ss.prefix)),
        );
        addLog(`Pushing all ${questionIdsToPush.length} response(s) for "${selectedWorkspace}"...`);
      } else {
        // Push only selected sub-sections
        for (const ssId of selectedPushItems) {
          const ss = subSections.find((s) => s.id === ssId);
          if (ss) {
            const matching = Object.keys(sourceResponses).filter((id) => id.startsWith(ss.prefix));
            questionIdsToPush.push(...matching);
          }
        }
        addLog(`Pushing ${questionIdsToPush.length} response(s) for selected sub-sections...`);
      }
    }

    // Merge responses into destination (standard workspaces)
    if (questionIdsToPush.length > 0) {
      addLog("Downloading destination Planning_UserResponses.json...");
      let destResponses = await downloadJsonFromFy(destFyYear, "Planning_UserResponses.json").catch(() => null);

      if (!destResponses) {
        destResponses = {
          companyName: planningResponses?.companyName || "",
          sectionName: planningResponses?.sectionName || "",
          lastUpdated: new Date().toISOString(),
          responses: {},
          sectionKey: planningResponses?.sectionKey || "",
        };
      }

      if (!destResponses.responses) destResponses.responses = {};

      for (const key of questionIdsToPush) {
        destResponses.responses[key] = sourceResponses[key];
      }
      destResponses.lastUpdated = new Date().toISOString();

      addLog(`Uploading ${questionIdsToPush.length} response(s) to destination...`);
      await uploadJsonToFy(destFyYear, "Planning_UserResponses.json", destResponses);
      addLog("Planning_UserResponses.json pushed successfully", "success");
    } else {
      addLog("No matching responses found in source to push", "info");
    }

    // Search source db.json for files tagged with "Planning_<prefix>_<questionId>" patterns
    // and copy each one to the destination, updating the destination db.json entry.
    await pushClientFilesForPlanningSection(prefix, questionIdsToPush);

    // Update Sections.json in destination
    await ensureSectionInDest("Planning", selectedWorkspace, prefix);

    addLog(`Planning "${selectedWorkspace}" push complete`, "success");
  };

  /**
   * Push all db.json-linked files for a planning workspace.
   * Uses the canonical pushDbFilesForPrefix with the workspace-level prefix
   * "Planning_<workspacePrefix>" so both client (section[]) and juggernaut (name)
   * are searched consistently. Planning_SPE_File entries are naturally excluded
   * because their section/name won't start with the workspace prefix pattern.
   */
  const pushClientFilesForPlanningSection = async (
    workspacePrefix: string,
    _questionIdsToPush?: string[],
  ) => {
    await pushDbFilesForPrefix(`Planning_${workspacePrefix}`);
  };

  // â”€â”€â”€ Push Execution â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const pushExecution = async () => {
    if (!selectedWorkspace) {
      addLog("No workspace selected", "error");
      return;
    }
    if (selectedPushItems.size === 0) {
      addLog("No options selected. Select at least one item (Tailoring Questions, ROMMs, or Substantive Procedures) to push.", "error");
      return;
    }

    const prefix =
      EXECUTION_PREFIX_MAP[selectedWorkspace] ??
      sectionsJson?.Execution?.[selectedWorkspace] ??
      "";
    addLog(`Pushing Execution workspace: ${selectedWorkspace} (prefix: ${prefix})`);

    const items = selectedPushItems;

    // Push only what is selected. Nothing is pushed when no option is selected.

    // Tailoring Questions
    if (items.has("tailoring")) {
      await pushTailoringQuestions(selectedWorkspace);
    }

    // ROMMs
    if (items.has("romms")) {
      await pushRomms(selectedWorkspace);
    }

    // â”€â”€ Substantive Procedures â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // When the "Substantive Procedures" card is selected, push:
    //   1. SubstantiveProcedures.json entries for this workspace
    //   2. All client + juggernaut db.json files for this workspace prefix
    //   3. Execution_<prefix>_Responses.json
    if (items.has("procedures")) {
      // 1. SubstantiveProcedures.json
      await pushSubstantiveProcedures(selectedWorkspace, prefix, new Set<string>());

      // 2. All workspace-level db.json files (client + juggernaut)
      const execDbPrefixes: string[] =
        EXECUTION_DB_PREFIX_MAP[selectedWorkspace] ??
        (prefix
          ? [`Execution_${prefix}`, `Execution_${selectedWorkspace}`]
          : [`Execution_${selectedWorkspace}`]);
      await pushDbFilesForPrefix([...new Set(execDbPrefixes)]);

      // 3. Response files
      await pushExecutionResponseFiles(prefix);
    }

    // Custom execution workspace: push all db.json-linked files directly
    if (items.has("workspace-files")) {
      const execDbPrefixes: string[] =
        EXECUTION_DB_PREFIX_MAP[selectedWorkspace] ??
        (prefix
          ? [`Execution_${prefix}`, `execution-substantive:${selectedWorkspace}`]
          : [`Execution_${selectedWorkspace}`, `execution-substantive:${selectedWorkspace}`]);
      await pushDbFilesForPrefix([...new Set(execDbPrefixes)]);
    }

    // Update Sections.json only when at least one section was pushed
    if (items.size > 0) {
      await ensureSectionInDest("Execution", selectedWorkspace, prefix);
    }

    addLog(`Execution "${selectedWorkspace}" push complete`, "success");
  };

  // â”€â”€â”€ Push Concluding â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const pushConcluding = async () => {
    if (!selectedWorkspace) {
      addLog("No workspace selected", "error");
      return;
    }

    const prefix =
      CONCLUDING_PREFIX_MAP_SHORT[selectedWorkspace] ??
      sectionsJson?.ConcludingProcedures?.[selectedWorkspace] ??
      "";
    addLog(`Pushing Concluding workspace: ${selectedWorkspace} (prefix: ${prefix})`);

    // For "Evaluate Audit Results Including Misstatements", push Libraries_Misstatement.json as a single unit
    if (selectedWorkspace === "Evaluate Audit Results Including Misstatements") {
      addLog("Pushing Libraries_Misstatement.json...");
      const misstatementData = await downloadJsonFromFy(sourceFyYear, "Libraries_Misstatement.json").catch(() => null);
      if (misstatementData) {
        // Strip signoffs from every misstatement entry before pushing.
        const topKey =
          misstatementData?.Libraries_Misstatement !== undefined
            ? "Libraries_Misstatement"
            : "libraries_Misstatement";
        if (misstatementData[topKey]?.misstatements) {
          const misObj = misstatementData[topKey].misstatements;
          for (const id of Object.keys(misObj)) {
            misObj[id] = stripSignoff(misObj[id]);
          }
        }
        await uploadJsonToFy(destFyYear, "Libraries_Misstatement.json", misstatementData);
        addLog("Libraries_Misstatement.json pushed", "success");
      } else {
        addLog("Libraries_Misstatement.json not found in source", "info");
      }

      await ensureSectionInDest("ConcludingProcedures", selectedWorkspace, prefix);
      addLog(`Concluding "${selectedWorkspace}" push complete`, "success");
      return;
    }

    const items = selectedPushItems;
    if (items.size === 0) {
      const isExecStyle =
        CONCLUDING_PROCEDURES_WITH_DIVISIONS.includes(selectedWorkspace) ||
        !(selectedWorkspace in CONCLUDING_PREFIX_MAP_SHORT);
      addLog(
        isExecStyle
          ? "No options selected. Select at least one item (Tailoring Questions, ROMMs, or Substantive Procedures) to push."
          : "No options selected. Select at least one item (Tailoring Questions, Workspace Files) to push.",
        "error",
      );
      return;
    }

    // Concluding Procedures tailoring questions
    if (items.has("tailoring")) {
      await pushConcludingTailoringQuestions(selectedWorkspace);
    }

    // For GC, L&C, and all custom concluding workspaces: execution-style push
    // (ROMMs + Substantive Procedures). For all other standard workspaces, push
    // their db.json-linked files via the workspace-files block below.
    const isCustomConcludingPush = !(selectedWorkspace in CONCLUDING_PREFIX_MAP_SHORT);
    if (CONCLUDING_PROCEDURES_WITH_DIVISIONS.includes(selectedWorkspace) || isCustomConcludingPush) {
      if (items.has("romms")) {
        await pushRomms(selectedWorkspace);
      }
      if (items.has("procedures")) {
        await pushSubstantiveProcedures(selectedWorkspace, prefix);
        // For custom workspaces, also push concluding-specific db files
        if (isCustomConcludingPush) {
          const customDbPrefixes = [
            `ConcludingProcedures_${selectedWorkspace}`,
            ...(prefix ? [`Conclusion_${prefix}`] : []),
            `concludingprocedures-substantive:${selectedWorkspace}`,
          ];
          await pushDbFilesForPrefix(customDbPrefixes);
        }
      }
      await ensureSectionInDest("ConcludingProcedures", selectedWorkspace, prefix);
      addLog(`Concluding "${selectedWorkspace}" push complete`, "success");
      return;
    }

    if (items.has("workspace-files")) {
      // Hardcoded prefix map covering every section key format found in the codebase
      const CONCLUDING_PREFIX_MAP: Record<string, string[]> = {
        "CARO": ["Conclusion_CARO", "ConcludingProcedures_CARO"],
        "Audit Report & Opinion": ["ConcludingProcedures_AUDREP", "ConcludingProcedures_Audit Report & Opinion"],
        "Communication with those charged with governance": ["ConcludingProcedures_CTCWG", "ConcludingProcedures_Communication with those charged with governance"],
        "Subsequent Events": ["ConcludingProcedures_SUBSEV", "ConcludingProcedures_SE", "ConcludingProcedures_Subsequent Events", "Conclusion_SE"],
        "Litigations & Claims": ["ConcludingProcedures_LIT", "ConcludingProcedures_Litigations_Claims", "ConcludingProcedures_Litigations & Claims"],
        "Compliance with laws and regulations": ["ConcludingProcedures_CLR", "ConcludingProcedures_Compliance with laws and regulations"],
        "Review of estimates for management bias": ["ConcludingProcedures_REMB", "ConcludingProcedures_Review of estimates for management bias"],
        "Internal Audit": ["ConcludingProcedures_ITAUD", "ConcludingProcedures_Internal Audit"],
        "Going Concern": ["ConcludingProcedures_GC", "ConcludingProcedures_Going Concern"],
        "Obtain Written Representations": ["ConcludingProcedures_WR", "ConcludingProcedures_Obtain Written Representations"],
        "Evaluate Audit Results Including Misstatements": ["ConcludingProcedures_EARM", "ConcludingProcedures_Evaluate Audit Results Including Misstatements"],
      };
      const dbPrefixes: string[] =
        CONCLUDING_PREFIX_MAP[selectedWorkspace] ??
        [
          `ConcludingProcedures_${selectedWorkspace}`,
          `Conclusion_${prefix}`,
          `concludingprocedures-substantive:${selectedWorkspace}`,
        ];
      await pushDbFilesForPrefix(dbPrefixes);
    }

    // Update sections
    await ensureSectionInDest("ConcludingProcedures", selectedWorkspace, prefix);

    addLog(`Concluding "${selectedWorkspace}" push complete`, "success");
  };

  // â”€â”€â”€ Push Financial Data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const pushFinancialData = async () => {
    if (!finDataOption) {
      addLog("No financial data option selected", "error");
      return;
    }

    if (finDataOption === "trial-balance") {
      addLog("Pushing Trial Balance files...");
      for (const fileName of ["FinData_TrialBalance_Mapping.json", "FinData_MappingData.json"]) {
        try {
          const data = await downloadJsonFromFy(sourceFyYear, fileName);
          if (data) {
            await uploadJsonToFy(destFyYear, fileName, data);
            addLog(`${fileName} pushed`, "success");
          } else {
            addLog(`${fileName} not found in source`, "info");
          }
        } catch (err: any) {
          addLog(`Failed to push ${fileName}: ${err.message}`, "error");
        }
      }
    }

    if (finDataOption === "analytics") {
      addLog("Pushing Analytics files...");
      try {
        const data = await downloadJsonFromFy(sourceFyYear, "FinData_Formulae.json");
        if (data) {
          await uploadJsonToFy(destFyYear, "FinData_Formulae.json", data);
          addLog("FinData_Formulae.json pushed", "success");
        } else {
          addLog("FinData_Formulae.json not found in source", "info");
        }
      } catch (err: any) {
        addLog(`Failed to push FinData_Formulae.json: ${err.message}`, "error");
      }
    }
  };

  // â”€â”€â”€ Push Libraries â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const pushLibraries = async () => {
    if (!libraryType) {
      addLog("No library type selected", "error");
      return;
    }

    if (libraryType === "control-library") {
      await pushControlLibrary();
    } else if (libraryType === "deficiency-library") {
      await pushDeficiencyLibrary();
    } else if (libraryType === "misstatement-library") {
      await pushMisstatementLibrary();
    } else if (libraryType === "control-owner-library") {
      await pushControlOwnerLibrary();
    } else if (libraryType === "information-library") {
      await pushInformationLibrary();
    } else if (libraryType === "document-library") {
      await pushDocumentLibrary();
    }
  };

  // â”€â”€â”€ Sub-push helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const pushTailoringQuestions = async (workspaceName: string) => {
    if (!tailoringResponses || !tailoringResponses[workspaceName]) {
      addLog(`No tailoring questions found for ${workspaceName}`, "info");
      return;
    }

    addLog("Pushing tailoring questions...");
    let destTailor = await downloadJsonFromFy(
      destFyYear,
      "Execution_TailoringQuestions_Responses.json",
    ).catch(() => ({}));

    if (!destTailor) destTailor = {};
    destTailor[workspaceName] = tailoringResponses[workspaceName];

    await uploadJsonToFy(destFyYear, "Execution_TailoringQuestions_Responses.json", destTailor);
    addLog("Tailoring questions pushed", "success");
  };

  /**
   * Push the selected workspace's section from
   * ConcludingProcedures_TailoringQuestions_Responses.json to the destination,
   * then copy any files listed in each response's `documents` array from
   * source client/ folder â†’ destination client/ folder, updating dest db.json.
   */
  const pushConcludingTailoringQuestions = async (workspaceName: string) => {
    const sectionData = concludingTailoringResponses?.[workspaceName];
    if (!sectionData || Object.keys(sectionData).length === 0) {
      addLog(`No concluding tailoring questions found for "${workspaceName}"`, "info");
      return;
    }

    addLog(`Pushing ConcludingProcedures tailoring questions for "${workspaceName}"...`);

    // Merge the workspace section into the destination file
    let destTailor: Record<string, any> =
      (await downloadJsonFromFy(
        destFyYear,
        "ConcludingProcedures_TailoringQuestions_Responses.json",
      ).catch(() => null)) || {};

    destTailor[workspaceName] = sectionData;
    await uploadJsonToFy(
      destFyYear,
      "ConcludingProcedures_TailoringQuestions_Responses.json",
      destTailor,
    );
    addLog("ConcludingProcedures_TailoringQuestions_Responses.json pushed", "success");

    // Collect all document file names referenced inside the section
    const documentFiles = new Set<string>();
    for (const response of Object.values(sectionData) as any[]) {
      if (Array.isArray(response?.documents)) {
        for (const docName of response.documents) {
          if (typeof docName === "string" && docName.trim()) {
            documentFiles.add(docName.trim());
          }
        }
      }
    }

    if (documentFiles.size === 0) {
      addLog("No document files referenced in this section", "info");
      return;
    }

    addLog(`Found ${documentFiles.size} document file(s) to copy...`);

    // Load source db.json to get the full entry (reference, section, url)
    const db = sourceDb || (await getDbFromFy(sourceFyYear).catch(() => null));

    // Build the concluding prefix so we can filter to only relevant sections
    const concludingPrefix =
      CONCLUDING_PREFIX_MAP_SHORT[workspaceName] ??
      sectionsJson?.ConcludingProcedures?.[workspaceName] ??
      "";
    const concludingPrefixPatterns = [
      `ConcludingProcedures_${workspaceName}`,
      ...(concludingPrefix ? [`ConcludingProcedures_${concludingPrefix}`] : []),
    ];
    const matchesConcludingPrefix = (v: string) =>
      concludingPrefixPatterns.some(
        (p) => v === p || v.startsWith(p + "_") || v.startsWith(p + "-"),
      );

    for (const docName of documentFiles) {
      // Find source db.json entry (prefer client, fall back to juggernaut)
      const sourceEntry =
        (db?.client || []).find((f: any) => f.name === docName) ||
        (db?.juggernaut || []).find((f: any) => f.name === docName);

      // Only carry over sections relevant to this concluding workspace
      const relevantSections = Array.isArray(sourceEntry?.section)
        ? sourceEntry.section.filter((s: string) => matchesConcludingPrefix(s))
        : [];

      try {
        await copyFileBetweenFy(
          sourceFyYear,
          destFyYear,
          "client",
          "client",
          docName,
          relevantSections,
          sourceEntry?.reference ?? "",
        );
        addLog(`Copied document file: ${docName}`, "success");
      } catch (err: any) {
        addLog(`Failed to copy document "${docName}": ${err.message}`, "error");
      }
    }
  };

  // Removes the "signoff" key from a library entry before pushing it forward.
  const stripSignoff = (entry: any): any => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
    const { signoff: _s, ...rest } = entry;
    return rest;
  };

  const pushRomms = async (workspaceName: string) => {
    const wsRomms = getRommsForWorkspace(workspaceName);
    if (wsRomms.length === 0) {
      addLog(`No ROMMs found for ${workspaceName}`, "info");
      return;
    }

    addLog(`Pushing ${wsRomms.length} ROMM(s)...`);
    let destRommData = await downloadJsonFromFy(destFyYear, "Libraries_Romm.json").catch(() => null);
    if (!destRommData) destRommData = { romm_library: [] };
    if (!Array.isArray(destRommData.romm_library)) destRommData.romm_library = [];

    for (const romm of wsRomms) {
      const rommClean = stripSignoff(romm);
      const existIdx = destRommData.romm_library.findIndex(
        (r: any) => r["romm-id"] === romm["romm-id"],
      );
      if (existIdx >= 0) {
        destRommData.romm_library[existIdx] = rommClean;
      } else {
        destRommData.romm_library.push(rommClean);
      }
    }

    await uploadJsonToFy(destFyYear, "Libraries_Romm.json", destRommData);
    addLog(`${wsRomms.length} ROMM(s) pushed`, "success");
  };

  /**
   * Push substantive procedures for a workspace.
   * If `procsToInclude` is non-empty, only those procedure entries are pushed.
   * Otherwise all procedures for the workspace are pushed.
   * After updating SubstantiveProcedures.json, copies all db.json-linked files
   * for the workspace (client section + juggernaut name) to the destination.
   */
  const pushSubstantiveProcedures = async (
    workspaceName: string,
    prefix: string,
    procsToInclude?: Set<string>,
  ) => {
    if (!substantiveProcedures || !substantiveProcedures[workspaceName]) {
      addLog(`No substantive procedures found for ${workspaceName}`, "info");
      return;
    }

    const allProcedures = substantiveProcedures[workspaceName] as Record<string, any[]>;
    const selectedKeys =
      procsToInclude && procsToInclude.size > 0
        ? [...procsToInclude].filter((p) => p in allProcedures)
        : Object.keys(allProcedures);

    if (selectedKeys.length === 0) {
      addLog("No matching procedures to push", "info");
      return;
    }

    addLog(`Pushing ${selectedKeys.length} procedure(s): ${selectedKeys.join(", ")}...`);

    // Merge only selected procedures into the destination SubstantiveProcedures.json
    let destProcs: Record<string, any> =
      (await downloadJsonFromFy(destFyYear, "SubstantiveProcedures.json").catch(() => null)) || {};
    if (!destProcs[workspaceName]) destProcs[workspaceName] = {};

    for (const procName of selectedKeys) {
      // Push the procedure key but clear any signoff entries by resetting to an empty array.
      destProcs[workspaceName][procName] = [];
    }

    await uploadJsonToFy(destFyYear, "SubstantiveProcedures.json", destProcs);
    addLog("SubstantiveProcedures.json pushed", "success");

    // Build the db.json prefix list for the selected procedures.
    //
    // â€¢ If the caller passed specific procedures (procsToInclude non-empty),
    //   use ONLY the prefixes for those procedures so unrelated files stay out.
    //   Unknown procedures are logged but do NOT trigger a workspace-wide sweep.
    // â€¢ If all procedures are being pushed (procsToInclude empty / undefined),
    //   fall back to the whole-workspace prefix â€“ this is the "push all" path.
    const isPushingSpecific = procsToInclude && procsToInclude.size > 0;
    const procedureMap = PROCEDURE_DB_PREFIX_MAP[workspaceName] ?? {};
    const workspaceFallback: string[] =
      EXECUTION_DB_PREFIX_MAP[workspaceName] ??
      (prefix ? [`Execution_${prefix}`] : [`Execution_${workspaceName}`]);

    const spDbPrefixes = new Set<string>();

    if (isPushingSpecific) {
      for (const procName of selectedKeys) {
        const procPrefixes = procedureMap[procName];
        if (procPrefixes && procPrefixes.length > 0) {
          procPrefixes.forEach((p) => spDbPrefixes.add(p));
        } else {
          // No known prefix for this procedure â€“ skip rather than pulling everything
          addLog(`No specific db.json prefix for procedure "${procName}" â€” files skipped`, "info");
        }
      }
      // If none of the selected procedures had a known prefix, fall back so
      // the user at least gets something.
      if (spDbPrefixes.size === 0) {
        workspaceFallback.forEach((p) => spDbPrefixes.add(p));
      }
    } else {
      // Push-all path: use workspace-level prefix
      workspaceFallback.forEach((p) => spDbPrefixes.add(p));
    }

    await pushDbFilesForPrefix([...spDbPrefixes]);
  };

  const pushExecutionResponseFiles = async (prefix: string) => {
    const responseFileName = `Execution_${prefix}_Responses.json`;
    addLog(`Pushing ${responseFileName}...`);
    try {
      const data = await downloadJsonFromFy(sourceFyYear, responseFileName);
      if (data) {
        let destData = await downloadJsonFromFy(destFyYear, responseFileName).catch(() => ({}));
        if (!destData) destData = {};
        Object.assign(destData, data);
        await uploadJsonToFy(destFyYear, responseFileName, destData);
        addLog(`${responseFileName} pushed`, "success");
      } else {
        addLog(`${responseFileName} not found`, "info");
      }
    } catch (err: any) {
      addLog(`Failed to push ${responseFileName}: ${err.message}`, "error");
    }
  };

  // Legacy wrappers â€” both delegate to the canonical pushDbFilesForPrefix
  const pushAllFilesWithPrefix = async (filePrefix: string, _unused?: string) => {
    await pushDbFilesForPrefix(filePrefix);
  };

  const pushClientFilesForSection = async (sectionPrefix: string, _unused?: string) => {
    await pushDbFilesForPrefix(sectionPrefix);
  };

  /**
   * â”€â”€â”€ CANONICAL FILE-PUSH HELPER â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   * Accepts a single prefix string OR an array of prefix strings.
   * For every prefix it searches source db.json:
   *   â€¢ client[]     â€“ entries whose section[] has ANY value starting with the prefix
   *   â€¢ juggernaut[] â€“ entries whose `name` starts with the prefix
   *
   * Supports multiple prefixes because some phases store files under different
   * naming schemes (e.g. Concluding uses both "ConcludingProcedures_<Workspace Name>_*"
   * for Q&A uploads and "Conclusion_<PREFIX>_*" for document files).
   *
   * For every unique match it copies the file from sourceFyYear â†’ destFyYear and
   * creates/updates its entry in the destination db.json (section[] is merged, not replaced).
   */
  const pushDbFilesForPrefix = async (prefixes: string | string[]) => {
    const prefixList = typeof prefixes === "string" ? [prefixes] : prefixes;
    const displayPrefix = prefixList.join(" | ");

    const db = sourceDb || (await getDbFromFy(sourceFyYear).catch(() => null));
    if (!db) {
      addLog(`Cannot load source db.json (prefix: "${displayPrefix}")`, "info");
      return;
    }

    // Prefix matching with word-boundary check:
    // A value `v` matches prefix `p` when:
    //   â€¢ v === p              (exact match)
    //   â€¢ v starts with p + "_" (underscore separator, e.g. Execution_PPE_AL_linked)
    //   â€¢ v starts with p + "-" (hyphen separator, e.g. Execution_PPE_AL-something)
    // This prevents "Execution_PPE_AL" from matching "Execution_PPE_ALCompAnls".
    const matchesPrefix = (v: string, p: string) =>
      v === p || v.startsWith(p + "_") || v.startsWith(p + "-");

    // client: any section[] value matches ANY of the prefixes
    const clientMatches: any[] = (db.client || []).filter(
      (f: any) =>
        Array.isArray(f.section) &&
        f.section.some((s: string) => prefixList.some((p) => matchesPrefix(s, p))),
    );

    // juggernaut: name matches ANY of the prefixes
    const juggernautMatches: any[] = (db.juggernaut || []).filter(
      (f: any) =>
        typeof f.name === "string" &&
        prefixList.some((p) => matchesPrefix(f.name.replace(/\.json$/i, ""), p)),
    );

    const all = [
      ...clientMatches.map((f) => ({ ...f, _folder: "client" as const })),
      ...juggernautMatches.map((f) => ({ ...f, _folder: "juggernaut" as const })),
    ];

    if (all.length === 0) {
      addLog(`No db.json entries found for prefix "${displayPrefix}"`, "info");
      return;
    }

    addLog(`Found ${all.length} file(s) in db.json for prefix "${displayPrefix}" â€” copying...`);

    const seen = new Set<string>();
    for (const file of all) {
      // De-duplicate: a file may appear matched by more than one prefix
      if (seen.has(file.name)) continue;
      seen.add(file.name);
      try {
        // Only carry over the sections that actually matched the search prefix(es),
        // not the entire source section array. This prevents unrelated sections from
        // being copied into the destination db.json entry.
        const matchedSections = Array.isArray(file.section)
          ? file.section.filter((s: string) => prefixList.some((p) => matchesPrefix(s, p)))
          : [];
        await copyFileBetweenFy(
          sourceFyYear,
          destFyYear,
          file._folder,
          file._folder,
          file.name,
          matchedSections,
          file.reference ?? "",
        );
        addLog(`Copied ${file._folder} file: ${file.name}`, "success");
      } catch (err: any) {
        addLog(`Failed to copy "${file.name}": ${err.message}`, "error");
      }
    }
  };

  const ensureSectionInDest = async (
    area: "Planning" | "Execution" | "ConcludingProcedures",
    workspaceName: string,
    prefix: string,
  ) => {
    addLog(`Ensuring "${workspaceName}" exists in destination Sections.json...`);
    try {
      let destSections = await downloadJsonFromFy(destFyYear, "Sections.json");
      if (!destSections) destSections = { Planning: {}, Execution: {}, ConcludingProcedures: {} };
      if (!destSections[area]) destSections[area] = {};
      if (!destSections[area][workspaceName]) {
        destSections[area][workspaceName] = prefix;
        await uploadJsonToFy(destFyYear, "Sections.json", destSections);
        addLog(`Added "${workspaceName}" to destination Sections.json`, "success");
      } else {
        addLog(`"${workspaceName}" already exists in destination Sections.json`, "info");
      }
    } catch (err: any) {
      addLog(`Failed to update Sections.json: ${err.message}`, "error");
    }
  };

  // â”€â”€â”€ Library push functions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const pushControlLibrary = async () => {
    addLog("Pushing Control Library...");
    const selected = Array.from(selectedLibraryItems);
    if (selected.length === 0) {
      addLog("No controls selected", "error");
      return;
    }

    // â”€â”€ 1. Push Libraries_RCM.json entries (object keyed by control ID) â”€â”€
    const rcmData = await downloadJsonFromFy(sourceFyYear, "Libraries_RCM.json").catch(() => null);
    if (rcmData) {
      let destRcm = await downloadJsonFromFy(destFyYear, "Libraries_RCM.json").catch(() => null);

      // Handle both object format (libraries_RCM) and legacy array format (rcm_library)
      const sourceIsObject =
        rcmData.libraries_RCM && !Array.isArray(rcmData.libraries_RCM);

      if (sourceIsObject) {
        if (!destRcm) destRcm = { libraries_RCM: {} };
        if (!destRcm.libraries_RCM || Array.isArray(destRcm.libraries_RCM))
          destRcm.libraries_RCM = {};

        for (const controlId of selected) {
          if (rcmData.libraries_RCM[controlId] !== undefined) {
            destRcm.libraries_RCM[controlId] = stripSignoff(rcmData.libraries_RCM[controlId]);
            addLog(`Libraries_RCM.json: merged entry for ${controlId}`, "success");
          } else {
            addLog(`Libraries_RCM.json: no entry found for ${controlId}`, "info");
          }
        }
      } else {
        // Legacy array format fallback
        if (!destRcm) destRcm = { rcm_library: [] };
        if (!Array.isArray(destRcm.rcm_library)) destRcm.rcm_library = [];
        const rcmEntries = Array.isArray(rcmData.rcm_library) ? rcmData.rcm_library : [];
        for (const controlId of selected) {
          const entry = rcmEntries.find(
            (r: any) => r["control-id"] === controlId || r.id === controlId,
          );
          if (entry) {
            const entryClean = stripSignoff(entry);
            const existIdx = destRcm.rcm_library.findIndex(
              (r: any) =>
                (r["control-id"] || r.id) === (entry["control-id"] || entry.id),
            );
            if (existIdx >= 0) destRcm.rcm_library[existIdx] = entryClean;
            else destRcm.rcm_library.push(entryClean);
          }
        }
      }

      await uploadJsonToFy(destFyYear, "Libraries_RCM.json", destRcm);
      addLog("Libraries_RCM.json pushed", "success");
    }

    // â”€â”€ 2. For each selected control: push response JSON + db.json-linked files â”€â”€
    for (const controlId of selected) {
      // Push Libraries_InternalControlResponses_<controlId>.json
      const fileName = `Libraries_InternalControlResponses_${controlId}.json`;
      try {
        const data = await downloadJsonFromFy(sourceFyYear, fileName);
        if (data) {
          await uploadJsonToFy(destFyYear, fileName, data);
          addLog(`${fileName} pushed`, "success");
        }
      } catch {
        addLog(`${fileName} not found in source`, "info");
      }

      // Push all db.json-linked files (client section[] and juggernaut name) for this control
      await pushDbFilesForPrefix(`Libraries_InternalControlResponses_${controlId}`);
    }
  };

  const pushDeficiencyLibrary = async () => {
    addLog("Pushing Deficiency Library...");
    const selected = Array.from(selectedLibraryItems);
    if (selected.length === 0) {
      addLog("No deficiencies selected", "error");
      return;
    }

    // â”€â”€ 1. Push Libraries_Deficiency.json entries (object keyed by deficiency ID) â”€â”€
    const defData = await downloadJsonFromFy(sourceFyYear, "Libraries_Deficiency.json").catch(() => null);
    if (defData) {
      let destDef = await downloadJsonFromFy(destFyYear, "Libraries_Deficiency.json").catch(() => null);

      const sourceIsObject =
        defData.libraries_deficiency && !Array.isArray(defData.libraries_deficiency);

      if (sourceIsObject) {
        if (!destDef) destDef = { libraries_deficiency: {} };
        if (!destDef.libraries_deficiency || Array.isArray(destDef.libraries_deficiency))
          destDef.libraries_deficiency = {};

        for (const defId of selected) {
          if (defData.libraries_deficiency[defId] !== undefined) {
            destDef.libraries_deficiency[defId] = stripSignoff(defData.libraries_deficiency[defId]);
            addLog(`Libraries_Deficiency.json: merged entry for ${defId}`, "success");
          } else {
            addLog(`Libraries_Deficiency.json: no entry found for ${defId}`, "info");
          }
        }
      } else {
        // Legacy array format fallback
        if (!destDef) destDef = { deficiency_library: [] };
        if (!Array.isArray(destDef.deficiency_library)) destDef.deficiency_library = [];
        const entries = Array.isArray(defData.deficiency_library) ? defData.deficiency_library : [];
        for (const defId of selected) {
          const entry = entries.find(
            (d: any) => d["deficiency-id"] === defId || d.id === defId,
          );
          if (entry) {
            const entryClean = stripSignoff(entry);
            const existIdx = destDef.deficiency_library.findIndex(
              (d: any) =>
                (d["deficiency-id"] || d.id) === (entry["deficiency-id"] || entry.id),
            );
            if (existIdx >= 0) destDef.deficiency_library[existIdx] = entryClean;
            else destDef.deficiency_library.push(entryClean);
          }
        }
      }

      await uploadJsonToFy(destFyYear, "Libraries_Deficiency.json", destDef);
      addLog("Libraries_Deficiency.json pushed", "success");
    }

    // â”€â”€ 2. For each selected deficiency: push detail JSON + db.json-linked files â”€â”€
    for (const defId of selected) {
      // Push Libraries_Deficiency_<defId>.json
      const fileName = `Libraries_Deficiency_${defId}.json`;
      try {
        const data = await downloadJsonFromFy(sourceFyYear, fileName);
        if (data) {
          await uploadJsonToFy(destFyYear, fileName, data);
          addLog(`${fileName} pushed`, "success");
        }
      } catch {
        addLog(`${fileName} not found in source`, "info");
      }

      // Push all db.json-linked files for this deficiency
      // Section format from DocumentManager: Deficiency_<DEF-ID>_<itemId>
      await pushDbFilesForPrefix(`Deficiency_${defId}`);
    }
  };

  const pushMisstatementLibrary = async () => {
    addLog("Pushing Misstatement Library...");
    const selected = Array.from(selectedLibraryItems);
    if (selected.length === 0) {
      addLog("No misstatements selected", "error");
      return;
    }

    const misData = await downloadJsonFromFy(sourceFyYear, "Libraries_Misstatement.json").catch(() => null);
    if (!misData) {
      addLog("Libraries_Misstatement.json not found in source", "info");
      return;
    }

    // Resolve the nested misstatements object:
    // { Libraries_Misstatement: { misstatements: { "MISST-06": {...}, ... } } }
    const resolveObj = (d: any) =>
      d?.Libraries_Misstatement?.misstatements ??
      d?.libraries_Misstatement?.misstatements ??
      d?.misstatements;

    const sourceMisObj = resolveObj(misData);
    if (!sourceMisObj || Array.isArray(sourceMisObj)) {
      addLog("Unrecognised Libraries_Misstatement.json structure in source", "error");
      return;
    }

    let destMisFile = await downloadJsonFromFy(destFyYear, "Libraries_Misstatement.json").catch(() => null);

    // Build or reuse the destination structure mirroring the source format
    const topKey =
      misData?.Libraries_Misstatement !== undefined
        ? "Libraries_Misstatement"
        : "libraries_Misstatement";

    if (!destMisFile) {
      destMisFile = { [topKey]: { misstatements: {} } };
    }
    if (!destMisFile[topKey]) destMisFile[topKey] = { misstatements: {} };
    if (!destMisFile[topKey].misstatements) destMisFile[topKey].misstatements = {};

    const destMisObj = destMisFile[topKey].misstatements;

    for (const misId of selected) {
      if (sourceMisObj[misId] !== undefined) {
        destMisObj[misId] = stripSignoff(sourceMisObj[misId]);
        addLog(`Merged misstatement entry: ${misId}`, "success");
      } else {
        addLog(`No entry found for ${misId} in source`, "info");
      }
    }

    await uploadJsonToFy(destFyYear, "Libraries_Misstatement.json", destMisFile);
    addLog("Libraries_Misstatement.json pushed", "success");
  };

  const pushControlOwnerLibrary = async () => {
    addLog("Pushing Control Owner Library...");
    const selected = Array.from(selectedLibraryItems);
    if (selected.length === 0) {
      addLog("No control owners selected", "error");
      return;
    }

    const coData = await downloadJsonFromFy(sourceFyYear, "Libraries_ControlOwners.json").catch(() => null);
    if (!coData) {
      addLog("Libraries_ControlOwners.json not found in source", "info");
      return;
    }

    // Actual structure: { controlOwners: { "CO-1": { name, designation, ... }, ... } }
    const sourceIsObject =
      coData.controlOwners && !Array.isArray(coData.controlOwners);

    let destCo = await downloadJsonFromFy(destFyYear, "Libraries_ControlOwners.json").catch(() => null);

    if (sourceIsObject) {
      if (!destCo) destCo = { controlOwners: {} };
      if (!destCo.controlOwners || Array.isArray(destCo.controlOwners))
        destCo.controlOwners = {};

      for (const coId of selected) {
        if (coData.controlOwners[coId] !== undefined) {
          destCo.controlOwners[coId] = coData.controlOwners[coId];
          addLog(`Merged control owner: ${coId}`, "success");
        } else {
          addLog(`No entry found for ${coId} in source`, "info");
        }
      }
    } else {
      // Legacy array format fallback
      if (!destCo) destCo = { control_owners: [] };
      if (!Array.isArray(destCo.control_owners)) destCo.control_owners = [];
      const entries = Array.isArray(coData.control_owners) ? coData.control_owners : [];
      for (const ownerId of selected) {
        const entry = entries.find((o: any) => o.id === ownerId || o.name === ownerId);
        if (entry) {
          const existIdx = destCo.control_owners.findIndex(
            (o: any) => (o.id || o.name) === (entry.id || entry.name),
          );
          if (existIdx >= 0) destCo.control_owners[existIdx] = entry;
          else destCo.control_owners.push(entry);
        }
      }
    }

    await uploadJsonToFy(destFyYear, "Libraries_ControlOwners.json", destCo);
    addLog("Libraries_ControlOwners.json pushed", "success");
  };

  const pushInformationLibrary = async () => {
    addLog("Pushing Information Library...");
    const selected = Array.from(selectedLibraryItems);
    if (selected.length === 0) {
      addLog("No information items selected", "error");
      return;
    }

    const infoData = await downloadJsonFromFy(sourceFyYear, "Libraries_Information.json").catch(() => null);
    if (!infoData) {
      addLog("Libraries_Information.json not found in source", "info");
      return;
    }

    // Actual structure: { information: [ { information_name, file_name, ... }, ... ] }
    const sourceArr: any[] = Array.isArray(infoData.information)
      ? infoData.information
      : Array.isArray(infoData.information_library)
        ? infoData.information_library
        : [];

    // Determine the top-level key to preserve format
    const topKey = Array.isArray(infoData.information) ? "information" : "information_library";

    let destInfo = await downloadJsonFromFy(destFyYear, "Libraries_Information.json").catch(() => null);
    if (!destInfo) destInfo = { [topKey]: [] };
    if (!Array.isArray(destInfo[topKey])) destInfo[topKey] = [];

    // â”€â”€ 1. Merge selected entries into Libraries_Information.json â”€â”€
    for (const infoName of selected) {
      const entry = sourceArr.find((i: any) => i.information_name === infoName);
      if (!entry) {
        addLog(`No entry found for "${infoName}" in source`, "info");
        continue;
      }
      const entryClean = stripSignoff(entry);
      const existIdx = (destInfo[topKey] as any[]).findIndex(
        (i: any) => i.information_name === entry.information_name,
      );
      if (existIdx >= 0) destInfo[topKey][existIdx] = entryClean;
      else destInfo[topKey].push(entryClean);
      addLog(`Merged information entry: ${infoName}`, "success");
    }

    await uploadJsonToFy(destFyYear, "Libraries_Information.json", destInfo);
    addLog("Libraries_Information.json pushed", "success");

    // â”€â”€ 2. Copy client files referenced in file_name (replace if already exists; db.json is updated) â”€â”€
    const seenInRun = new Set<string>();

    for (const infoName of selected) {
      const entry = sourceArr.find((i: any) => i.information_name === infoName);
      if (!entry?.file_name) continue;

      const fileName: string = entry.file_name;
      if (seenInRun.has(fileName)) continue;
      seenInRun.add(fileName);

      try {
        // Replace file even if it exists; IPC handler updates or creates db.json entry (section: [] for new)
        await copyFileBetweenFy(sourceFyYear, destFyYear, "client", "client", fileName, []);
        addLog(`Copied client file: ${fileName}`, "success");
      } catch (err: any) {
        addLog(`Failed to copy client file "${fileName}": ${err.message}`, "error");
      }
    }
  };

  const pushDocumentLibrary = async () => {
    addLog("Pushing Document Library...");
    const selected = Array.from(selectedLibraryItems);
    if (selected.length === 0) {
      addLog("No documents selected", "error");
      return;
    }

    for (const fileName of selected) {
      try {
        // Pass destSections so the copy handler updates db.json (create or merge entry)
        await copyFileBetweenFy(sourceFyYear, destFyYear, "client", "client", fileName, [], "");
        addLog(`Copied: ${fileName}`, "success");
      } catch (err: any) {
        addLog(`Failed to copy ${fileName}: ${err.message}`, "error");
      }
    }
  };

  // â”€â”€â”€ Load library sub-items â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  useEffect(() => {
    if (activeCategory !== "libraries" || !libraryType || !sourceFyYear) return;

    const loadLibraryItems = async () => {
      setLibrarySubItems([]);
      setSelectedLibraryItems(new Set());

      try {
        if (libraryType === "control-library") {
          const rcm = await downloadJsonFromFy(sourceFyYear, "Libraries_RCM.json").catch(() => null);
          // libraries_RCM is an object keyed by control ID
          const rcmObj = rcm?.libraries_RCM ?? rcm?.rcm_library;
          if (rcmObj && !Array.isArray(rcmObj)) {
            setLibrarySubItems(
              Object.entries(rcmObj).map(([controlId, val]: [string, any]) => ({
                id: controlId,
                label: `${controlId} - ${val?.control_name || ""}`,
              })),
            );
          } else if (Array.isArray(rcmObj)) {
            // legacy array format fallback
            setLibrarySubItems(
              rcmObj.map((c: any) => ({
                id: c["control-id"] || c.id,
                label: `${c["control-id"] || c.id} - ${c.description || c.control_name || ""}`,
              })),
            );
          }
        } else if (libraryType === "deficiency-library") {
          const def = await downloadJsonFromFy(sourceFyYear, "Libraries_Deficiency.json").catch(() => null);
          // libraries_deficiency is an object keyed by deficiency ID
          const defObj = def?.libraries_deficiency ?? def?.deficiency_library;
          if (defObj && !Array.isArray(defObj)) {
            setLibrarySubItems(
              Object.entries(defObj).map(([defId, val]: [string, any]) => ({
                id: defId,
                label: `${defId} - ${val?.description || ""}`,
              })),
            );
          } else if (Array.isArray(defObj)) {
            // legacy array format fallback
            setLibrarySubItems(
              defObj.map((d: any) => ({
                id: d["deficiency-id"] || d.id,
                label: `${d["deficiency-id"] || d.id} - ${d.description || ""}`,
              })),
            );
          }
        } else if (libraryType === "misstatement-library") {
          const mis = await downloadJsonFromFy(sourceFyYear, "Libraries_Misstatement.json").catch(() => null);
          // Actual structure: { Libraries_Misstatement: { misstatements: { "MISST-06": {...}, ... } } }
          const misObj: any =
            mis?.Libraries_Misstatement?.misstatements ??
            mis?.libraries_Misstatement?.misstatements ??
            mis?.misstatements;
          if (misObj && !Array.isArray(misObj)) {
            setLibrarySubItems(
              Object.entries(misObj).map(([misId, val]: [string, any]) => ({
                id: misId,
                label: `${misId}${val?.companyName ? ` â€” ${val.companyName}` : ""}`,
              })),
            );
          } else if (Array.isArray(misObj)) {
            // legacy array fallback
            setLibrarySubItems(
              misObj.map((m: any) => ({
                id: m.id || m["misstatement-id"],
                label: `${m.id || m["misstatement-id"]} - ${m.workspace || ""} (${m.type || ""})`,
              })),
            );
          }
        } else if (libraryType === "control-owner-library") {
          const co = await downloadJsonFromFy(sourceFyYear, "Libraries_ControlOwners.json").catch(() => null);
          // Actual structure: { controlOwners: { "CO-1": { name, designation, ... }, ... } }
          const coObj = co?.controlOwners ?? co?.control_owners;
          if (coObj && !Array.isArray(coObj)) {
            setLibrarySubItems(
              Object.entries(coObj).map(([coId, val]: [string, any]) => ({
                id: coId,
                label: `${coId} â€” ${val?.name || ""}${val?.designation ? ` (${val.designation})` : ""}`,
              })),
            );
          } else if (Array.isArray(coObj)) {
            // legacy array fallback
            setLibrarySubItems(
              coObj.map((o: any) => ({
                id: o.id || o.name,
                label: o.name || o.id,
              })),
            );
          }
        } else if (libraryType === "information-library") {
          const info = await downloadJsonFromFy(sourceFyYear, "Libraries_Information.json").catch(() => null);
          // Actual structure: { information: [ { information_name, file_name, ... }, ... ] }
          const infoArr = info?.information ?? info?.information_library;
          if (Array.isArray(infoArr)) {
            setLibrarySubItems(
              infoArr.map((i: any) => ({
                id: i.information_name,
                label: i.information_name,
              })),
            );
          }
        } else if (libraryType === "document-library") {
          const db = sourceDb || (await getDbFromFy(sourceFyYear).catch(() => null));
          if (db?.client) {
            setLibrarySubItems(
              db.client.map((f: any) => ({
                id: f.name,
                label: f.name,
              })),
            );
          }
        }
      } catch (err) {
        console.error("Failed to load library items", err);
      }
    };

    loadLibraryItems();
  }, [libraryType, activeCategory, sourceFyYear, sourceDb]);

  // â”€â”€â”€ RENDER â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const workspaces = getWorkspaces(activeCategory);
  const procedures = selectedWorkspace ? getProceduresForWorkspace(selectedWorkspace) : [];
  const wsRomms = selectedWorkspace ? getRommsForWorkspace(selectedWorkspace) : [];

  return (
    <div className="flex h-full flex-col">
      {/* Top bar: Destination engagement selector */}
      <div className="flex items-center gap-4 border-b border-white/10 bg-black/30 px-6 py-3">
        <span className="text-sm font-medium text-white/70">Push to:</span>
        <Select value={selectedEngagementId} onValueChange={handleEngagementSelect}>
          <SelectTrigger className="w-[400px] bg-black/40 border-white/20">
            <SelectValue placeholder="Select destination engagement..." />
          </SelectTrigger>
          <SelectContent>
            {engagements.map((eng) => (
              <SelectItem key={eng.id} value={eng.id}>
                {eng.engagement_name || eng.id}
                {eng.client_name ? ` (${eng.client_name})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {destFyYear && (
          <span className="text-xs text-white/50">
            Source: {sourceFyYear} â†’ Dest: {destFyYear}
          </span>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar: Categories + Workspaces */}
        <div className="w-72 flex-shrink-0 overflow-y-auto border-r border-white/10 bg-black/20">
          <div className="p-3 space-y-1">
            {visibleCategoryItems.map((cat) => {
              const Icon = cat.icon;
              const isExpanded = expandedCategory === cat.id;
              const isActive = activeCategory === cat.id;
              const catWorkspaces = getWorkspaces(cat.id);
              const hasSub =
                cat.id === "planning" ||
                cat.id === "execution" ||
                cat.id === "concluding";

              return (
                <div key={cat.id}>
                  <button
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setExpandedCategory(isExpanded ? null : cat.id);
                      setSelectedWorkspace("");
                      setSelectedPushItems(new Set());
                      setLibraryType("");
                      setFinDataOption("");
                    }}
                    className={`group flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-all ${
                      isActive
                        ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? "text-[#4da3ff]" : "text-white/50"}`} />
                    <span className="flex-1 text-left font-medium">{cat.title}</span>
                    {hasSub && (
                      <span className="text-white/40">
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </span>
                    )}
                  </button>

                  {/* Sub-items for Planning/Execution/Concluding */}
                  {hasSub && isExpanded && catWorkspaces.length > 0 && (
                    <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-3">
                      {catWorkspaces.map((ws) => (
                        <button
                          key={ws.name}
                          onClick={() => {
                            setActiveCategory(cat.id);
                            setSelectedWorkspace(ws.name);
                            if (cat.id === "execution") {
                              setSelectedPushItems(new Set(["tailoring", "romms", "procedures"]));
                            } else if (cat.id === "concluding") {
                              const isExecStyleWs =
                                CONCLUDING_PROCEDURES_WITH_DIVISIONS.includes(ws.name) ||
                                !(ws.name in CONCLUDING_PREFIX_MAP_SHORT);
                              setSelectedPushItems(
                                isExecStyleWs
                                  ? new Set(["tailoring", "romms", "procedures"])
                                  : new Set(["tailoring", "workspace-files"])
                              );
                            } else if (cat.id === "planning") {
                              const isCustomPlanningWs = !(ws.name in PLANNING_PREFIX_MAP);
                              setSelectedPushItems(isCustomPlanningWs ? new Set(["tailoring", "romms", "procedures"]) : new Set());
                            } else {
                              setSelectedPushItems(new Set());
                            }
                            setExecSubView("all");
                          }}
                          className={`w-full rounded px-2 py-1.5 text-left text-xs transition-all ${
                            selectedWorkspace === ws.name && activeCategory === cat.id
                              ? "bg-[#4da3ff]/20 text-white font-medium"
                              : "text-white/60 hover:bg-white/5 hover:text-white"
                          }`}
                        >
                          {ws.name}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Sub-items for Financial Data (hardcoded) */}
                  {cat.id === "financial-data" && isExpanded && (
                    <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-3">
                      {FINANCIAL_DATA_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => {
                            setActiveCategory("financial-data");
                            setFinDataOption(opt.id);
                          }}
                          className={`w-full rounded px-2 py-1.5 text-left text-xs transition-all ${
                            finDataOption === opt.id && activeCategory === "financial-data"
                              ? "bg-[#4da3ff]/20 text-white font-medium"
                              : "text-white/60 hover:bg-white/5 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Sub-items for Libraries (hardcoded) */}
                  {cat.id === "libraries" && isExpanded && (
                    <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-3">
                      {LIBRARY_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => {
                            setActiveCategory("libraries");
                            setLibraryType(opt.id);
                            setSelectedLibraryItems(new Set());
                          }}
                          className={`w-full rounded px-2 py-1.5 text-left text-xs transition-all ${
                            libraryType === opt.id && activeCategory === "libraries"
                              ? "bg-[#4da3ff]/20 text-white font-medium"
                              : "text-white/60 hover:bg-white/5 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main content area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            {/* Planning content */}
            {activeCategory === "planning" && renderPlanningContent()}

            {/* Execution content */}
            {activeCategory === "execution" && renderExecutionContent()}

            {/* Concluding content */}
            {activeCategory === "concluding" && renderConcludingContent()}

            {/* Financial Data content */}
            {activeCategory === "financial-data" && renderFinancialDataContent()}

            {/* Libraries content */}
            {activeCategory === "libraries" && renderLibrariesContent()}
          </div>

          {/* Push log & action bar */}
          <div className="border-t border-white/10 bg-black/30">
            {pushLog.length > 0 && (
              <div className="max-h-40 overflow-y-auto border-b border-white/10 p-3">
                {pushLog.map((log, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs py-0.5">
                    <span className="text-white/40 w-16 flex-shrink-0">{log.timestamp}</span>
                    {log.status === "success" && <Check className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />}
                    {log.status === "error" && <AlertCircle className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />}
                    {log.status === "info" && <ArrowRight className="h-3 w-3 text-blue-400 mt-0.5 flex-shrink-0" />}
                    <span
                      className={
                        log.status === "error"
                          ? "text-red-400"
                          : log.status === "success"
                          ? "text-green-400"
                          : "text-white/70"
                      }
                    >
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between px-6 py-3">
              <span className="text-xs text-white/50">
                {isPushing ? "Push in progress..." : "Ready to push"}
              </span>
              <Button
                onClick={handlePush}
                disabled={isPushing || !destFyYear}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isPushing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Pushing...
                  </>
                ) : (
                  <>
                    <ArrowRight className="mr-2 h-4 w-4" /> Push to Destination
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // â”€â”€â”€ Render helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function renderPlanningContent() {
    if (!selectedWorkspace) {
      return renderPlaceholder("Select a Planning workspace from the left panel to view and push data.");
    }

    const prefix = PLANNING_PREFIX_MAP[selectedWorkspace] || sectionsJson?.Planning?.[selectedWorkspace] || "";
    const sourceResponses = planningResponses?.responses || {};

    // â”€â”€ Materiality: push as whole, no sub-sections â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    if (selectedWorkspace === "Materiality") {
      const existingIds = MATERIALITY_QUESTION_IDS.filter((id) => id in sourceResponses);
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Materiality</h2>
          <p className="text-sm text-white/60">
            All Materiality responses will be pushed as a whole unit.
          </p>
          <div className="rounded-lg border border-white/10 bg-black/20 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              <span>
                {existingIds.length} response(s) found in source
              </span>
            </div>
            {existingIds.length > 0 && (
              <div className="ml-6 flex flex-wrap gap-1.5 pt-1">
                {existingIds.map((id) => (
                  <span key={id} className="rounded bg-white/10 px-2 py-0.5 font-mono text-xs text-white/60">
                    {id}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              All linked client files (Planning_{prefix}_*)
            </div>
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              Sections.json entry
            </div>
          </div>
        </div>
      );
    }

    // â”€â”€ Preliminary Analytical Procedures: push as whole â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    if (selectedWorkspace === "Preliminary Analytical Procedures") {
      const existingIds = PAP_QUESTION_IDS.filter((id) => id in sourceResponses);
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Preliminary Analytical Procedures</h2>
          <p className="text-sm text-white/60">
            All Preliminary Analytical Procedures data will be pushed as a whole unit.
          </p>
          <div className="rounded-lg border border-white/10 bg-black/20 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              <span>{existingIds.length} data item(s) found in source</span>
            </div>
            {existingIds.length > 0 && (
              <div className="ml-6 flex flex-wrap gap-1.5 pt-1">
                {existingIds.map((id) => (
                  <span key={id} className="rounded bg-white/10 px-2 py-0.5 font-mono text-xs text-white/60 break-all">
                    {id}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              All linked client files (Planning_{prefix}_*)
            </div>
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              Sections.json entry
            </div>
          </div>
        </div>
      );
    }

    // â”€â”€ IT Risk Assessment: no sub-divisions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    if (selectedWorkspace === "IT Risk Assessment") {
      const existingIds = IT_RISK_QUESTION_IDS.filter((id) => id in sourceResponses);
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">IT Risk Assessment</h2>
          <p className="text-sm text-white/60">
            All IT Risk Assessment responses will be pushed.
          </p>
          <div className="rounded-lg border border-white/10 bg-black/20 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              <span>{existingIds.length} response(s) found in source</span>
            </div>
            {existingIds.length > 0 && (
              <div className="ml-6 flex flex-wrap gap-1.5 pt-1">
                {existingIds.map((id) => (
                  <span key={id} className="rounded bg-white/10 px-2 py-0.5 font-mono text-xs text-white/60">
                    {id}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              All linked client files (Planning_{prefix}_*)
            </div>
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              Sections.json entry
            </div>
          </div>
        </div>
      );
    }

    // â”€â”€ Custom workspace (not in any hardcoded standard map) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // Single "Push full procedure" card â€” finds all db.json files whose
    // section/name starts with "Planning_<prefix>_" and pushes them.
    if (!(selectedWorkspace in PLANNING_PREFIX_MAP)) {
      const sectionData = tailoringResponses?.[selectedWorkspace];
      const questionCount = sectionData ? Object.keys(sectionData).length : 0;
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">{selectedWorkspace}</h2>
          <p className="text-sm text-white/60">
            Only selected items are pushed. All options are selected by default; deselect what you don&apos;t need.
          </p>
          <div className="space-y-3">
            <PushOptionCard
              title="Tailoring Questions"
              description={`From Execution_TailoringQuestions_Responses.json \u2192 "${selectedWorkspace}" section`}
              isSelected={selectedPushItems.has("tailoring")}
              onToggle={() => togglePushItem("tailoring")}
              hasData={questionCount > 0}
            />
            <PushOptionCard
              title={`ROMMs (${wsRomms.length})`}
              description="Push ROMM entries for this workspace from Libraries_Romm.json"
              isSelected={selectedPushItems.has("romms")}
              onToggle={() => togglePushItem("romms")}
              hasData={wsRomms.length > 0}
            />
            <PushOptionCard
              title={`Substantive Procedures${procedures.length > 0 ? ` (${procedures.length})` : ""}`}
              description={`Push SubstantiveProcedures.json, all linked client & juggernaut files (Planning_${prefix}_*)`}
              isSelected={selectedPushItems.has("procedures")}
              onToggle={() => togglePushItem("procedures")}
              hasData={procedures.length > 0}
            />
          </div>
        </div>
      );
    }

    // â”€â”€ Workspaces with sub-sections â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const subSections = PLANNING_SUBSECTIONS[selectedWorkspace] || [];

    // Count how many source responses exist per sub-section
    const subSectionCounts = subSections.map((ss) => ({
      ...ss,
      count: Object.keys(sourceResponses).filter((id) => id.startsWith(ss.prefix)).length,
    }));

    const allSelected = subSections.length > 0 && subSections.every((ss) => selectedPushItems.has(ss.id));

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{selectedWorkspace}</h2>
          {subSections.length > 0 && (
            <button
              onClick={() => {
                if (allSelected) {
                  setSelectedPushItems(new Set());
                } else {
                  setSelectedPushItems(new Set(subSections.map((ss) => ss.id)));
                }
              }}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          )}
        </div>
        <p className="text-sm text-white/60">
          Select sub-sections to push, or leave all unchecked to push everything.
        </p>

        {subSections.length === 0 ? (
          <p className="text-sm text-white/40">No sub-sections defined for this workspace.</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {subSectionCounts.map((ss) => {
              const isSelected = selectedPushItems.has(ss.id);
              return (
                <div
                  key={ss.id}
                  onClick={() => {
                    const next = new Set(selectedPushItems);
                    if (isSelected) next.delete(ss.id);
                    else next.add(ss.id);
                    setSelectedPushItems(next);
                  }}
                  className={`cursor-pointer rounded-lg border p-4 transition-all ${
                    isSelected
                      ? "border-blue-500/50 bg-blue-500/10"
                      : "border-white/10 bg-black/20 hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white leading-snug">
                        {ss.label}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs text-white/50">
                          {ss.prefix}*
                        </span>
                        <span className="text-xs text-white/40">
                          {ss.count} response{ss.count !== 1 ? "s" : ""} in source
                        </span>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-white/20 cursor-pointer"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="rounded-lg border border-white/10 bg-black/20 p-3 mt-2">
          <div className="text-xs text-white/50">
            If nothing is selected, all sub-sections will be pushed. Client files and Sections.json entry are always included.
          </div>
        </div>
      </div>
    );
  }

  function renderExecutionContent() {
    if (!selectedWorkspace) {
      return renderPlaceholder("Select an Execution workspace from the left panel to view and push data.");
    }

    const prefix =
      EXECUTION_PREFIX_MAP[selectedWorkspace] ??
      sectionsJson?.Execution?.[selectedWorkspace] ??
      "";


    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">{selectedWorkspace}</h2>
        <p className="text-sm text-white/60">
          Only selected items are pushed. All options are selected by default; deselect what you donâ€™t need.
        </p>

        {/* Push options */}
        <div className="space-y-3">
          {/* Tailoring Questions */}
          <PushOptionCard
            title="Tailoring Questions"
            description={`From Execution_TailoringQuestions_Responses.json â†’ "${selectedWorkspace}" section`}
            isSelected={selectedPushItems.has("tailoring")}
            onToggle={() => togglePushItem("tailoring")}
            hasData={!!tailoringResponses?.[selectedWorkspace]}
          />

          {/* ROMMs */}
          <PushOptionCard
            title={`ROMMs (${wsRomms.length})`}
            description="Push ROMM entries for this workspace from Libraries_Romm.json"
            isSelected={selectedPushItems.has("romms")}
            onToggle={() => togglePushItem("romms")}
            hasData={wsRomms.length > 0}
          />

          {/* Substantive Procedures */}
          <PushOptionCard
            title={`Substantive Procedures${procedures.length > 0 ? ` (${procedures.length})` : ""}`}
            description={`Push SubstantiveProcedures.json, all linked client & juggernaut files, and Execution_${prefix}_Responses.json`}
            isSelected={selectedPushItems.has("procedures")}
            onToggle={() => togglePushItem("procedures")}
            hasData={procedures.length > 0}
          />

        </div>
      </div>
    );
  }

  function renderConcludingContent() {
    if (!selectedWorkspace) {
      return renderPlaceholder("Select a Concluding Procedures workspace from the left panel.");
    }

    const prefix =
      CONCLUDING_PREFIX_MAP_SHORT[selectedWorkspace] ??
      sectionsJson?.ConcludingProcedures?.[selectedWorkspace] ??
      "";
    // Custom: not a standard concluding workspace
    const isCustomConcluding = !(selectedWorkspace in CONCLUDING_PREFIX_MAP_SHORT);
    // Custom workspaces and the explicitly listed GC/L&C get execution-style
    // Tailoring + ROMMs + Substantive Procedures UI.
    // All other standard workspaces keep the original Workspace Files UI.
    const hasProcedureDivision =
      CONCLUDING_PROCEDURES_WITH_DIVISIONS.includes(selectedWorkspace) || isCustomConcluding;

    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">{selectedWorkspace}</h2>

        {selectedWorkspace === "Evaluate Audit Results Including Misstatements" ? (
          <div className="rounded-lg border border-white/10 bg-black/20 p-4">
            <div className="text-sm font-medium text-white">Push Libraries_Misstatement.json</div>
            <div className="text-xs text-white/50 mt-1">
              The entire misstatement library file will be pushed to the destination.
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-white/60">
              Only selected items are pushed. All options are selected by default; deselect what you donâ€™t need.
            </p>

            <div className="space-y-3">
              {/* Tailoring Questions */}
              {(() => {
                const sectionData = concludingTailoringResponses?.[selectedWorkspace];
                const questionCount = sectionData ? Object.keys(sectionData).length : 0;
                // Count referenced document files
                let docCount = 0;
                if (sectionData) {
                  const seen = new Set<string>();
                  for (const resp of Object.values(sectionData) as any[]) {
                    if (Array.isArray(resp?.documents)) {
                      for (const d of resp.documents) {
                        if (typeof d === "string" && d.trim() && !seen.has(d)) {
                          seen.add(d);
                          docCount++;
                        }
                      }
                    }
                  }
                }
                return (
                  <PushOptionCard
                    title="Tailoring Questions"
                    description={`From ConcludingProcedures_TailoringQuestions_Responses.json â†’ "${selectedWorkspace}" section${docCount > 0 ? ` Â· ${docCount} document file${docCount !== 1 ? "s" : ""} will also be copied` : ""}`}
                    isSelected={selectedPushItems.has("tailoring")}
                    onToggle={() => togglePushItem("tailoring")}
                    hasData={questionCount > 0}
                  />
                );
              })()}

              {/* For GC, L&C, and all custom concluding workspaces: show ROMMs + Substantive Procedures */}
              {hasProcedureDivision ? (
                <>
                  <PushOptionCard
                    title={`ROMMs (${wsRomms.length})`}
                    description="Push ROMM entries for this workspace from Libraries_Romm.json"
                    isSelected={selectedPushItems.has("romms")}
                    onToggle={() => togglePushItem("romms")}
                    hasData={wsRomms.length > 0}
                  />
                  <PushOptionCard
                    title={`Substantive Procedures${procedures.length > 0 ? ` (${procedures.length})` : ""}`}
                    description={`Push SubstantiveProcedures.json, all linked client & juggernaut files, and ConcludingProcedures_${prefix}_Substantive.json`}
                    isSelected={selectedPushItems.has("procedures")}
                    onToggle={() => togglePushItem("procedures")}
                    hasData={procedures.length > 0}
                  />
                </>
              ) : (
                /* Workspace files (db.json-linked) for all other concluding workspaces */
                <PushOptionCard
                  title="Workspace Files"
                  description={`Push all files for this workspace from db.json (prefixes like "ConcludingProcedures_${prefix}" or "Conclusion_${prefix}")`}
                  isSelected={selectedPushItems.has("workspace-files")}
                  onToggle={() => togglePushItem("workspace-files")}
                  hasData={true}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  function renderFinancialDataContent() {
    if (!finDataOption) {
      return renderPlaceholder("Select Trial Balance or Analytics from the left panel.");
    }

    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">
          {finDataOption === "trial-balance" ? "Trial Balance" : "Analytics"}
        </h2>
        <div className="rounded-lg border border-white/10 bg-black/20 p-4">
          {finDataOption === "trial-balance" ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-white/80">
                <Check className="h-4 w-4 text-green-400" />
                FinData_TrialBalance_Mapping.json
              </div>
              <div className="flex items-center gap-2 text-sm text-white/80">
                <Check className="h-4 w-4 text-green-400" />
                FinData_MappingData.json
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Check className="h-4 w-4 text-green-400" />
              FinData_Formulae.json
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderLibrariesContent() {
    if (!libraryType) {
      return renderPlaceholder("Select a library type from the left panel.");
    }

    const libLabel = LIBRARY_OPTIONS.find((l) => l.id === libraryType)?.label || libraryType;

    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">{libLabel}</h2>
        <p className="text-sm text-white/60">Select items to push to the destination engagement.</p>

        {librarySubItems.length === 0 ? (
          <p className="text-sm text-white/40">No items found in source.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (selectedLibraryItems.size === librarySubItems.length) {
                    setSelectedLibraryItems(new Set());
                  } else {
                    setSelectedLibraryItems(new Set(librarySubItems.map((i) => i.id)));
                  }
                }}
              >
                {selectedLibraryItems.size === librarySubItems.length ? "Deselect All" : "Select All"}
              </Button>
              <span className="text-xs text-white/50">
                {selectedLibraryItems.size} of {librarySubItems.length} selected
              </span>
            </div>
            <div className="space-y-1 max-h-[50vh] overflow-y-auto">
              {librarySubItems.map((item) => {
                const isSelected = selectedLibraryItems.has(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => {
                      const next = new Set(selectedLibraryItems);
                      if (isSelected) next.delete(item.id);
                      else next.add(item.id);
                      setSelectedLibraryItems(next);
                    }}
                    className={`cursor-pointer flex items-center gap-3 rounded-md border p-2.5 transition-all ${
                      isSelected
                        ? "border-blue-500/50 bg-blue-500/10"
                        : "border-white/10 bg-black/20 hover:bg-white/5"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="h-4 w-4 rounded border-white/20"
                    />
                    <span className="text-sm text-white/80 truncate">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  function renderPlaceholder(message: string) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/5 border border-white/10">
            <ArrowRight className="h-8 w-8 text-white/30" />
          </div>
          <p className="text-sm text-white/50 max-w-md">{message}</p>
        </div>
      </div>
    );
  }

  function togglePushItem(item: string) {
    const next = new Set(selectedPushItems);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    setSelectedPushItems(next);
  }
}

// â”€â”€â”€ Sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function PushOptionCard({
  title,
  description,
  isSelected,
  onToggle,
  hasData,
}: {
  title: string;
  description: string;
  isSelected: boolean;
  onToggle: () => void;
  hasData: boolean;
}) {
  return (
    <div
      onClick={onToggle}
      className={`cursor-pointer rounded-lg border p-4 transition-all ${
        isSelected
          ? "border-blue-500/50 bg-blue-500/10"
          : "border-white/10 bg-black/20 hover:bg-white/5"
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-white">{title}</div>
          <div className="text-xs text-white/50 mt-0.5">{description}</div>
        </div>
        <div className="flex items-center gap-2">
          {!hasData && (
            <span className="text-xs text-amber-400/70">No data</span>
          )}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {}}
            className="h-4 w-4 rounded border-white/20"
          />
        </div>
      </div>
    </div>
  );
}
